import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  Users, Mail, Bell, TrendingUp, CheckCircle, Clock, 
  Trash2, Eye, X, LogOut, ChevronDown, UserCheck, UserX, Calendar,
  BookOpen, Plus, Edit2, Save, GraduationCap, FileText, Video, ClipboardList, ChevronRight,
  Trophy, Star, Medal, Zap, Download, FileSpreadsheet, Briefcase, Search, Filter, 
  User, UserCog, Award, MessageSquare, Key, Archive, MoreVertical, Activity, Target
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Moved outside to prevent re-creation on every render
const StatusBadge = ({ status }) => {
  const colors = {
    pending: 'bg-yellow-500/20 text-yellow-400',
    approved: 'bg-green-500/20 text-green-400',
    rejected: 'bg-red-500/20 text-red-400',
    unread: 'bg-blue-500/20 text-blue-400',
    read: 'bg-gray-500/20 text-gray-400',
    replied: 'bg-green-500/20 text-green-400'
  };
  const displayStatus = status || 'pending';
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[displayStatus] || colors.pending}`}>
      {displayStatus.charAt(0).toUpperCase() + displayStatus.slice(1)}
    </span>
  );
};

const Admin = () => {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('enrollments');
  const [stats, setStats] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [courses, setCourses] = useState([]);
  const [teacherApplications, setTeacherApplications] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [leaderboard, setLeaderboard] = useState(null);
  const [selectedStudentGamification, setSelectedStudentGamification] = useState(null);
  const [showGamificationModal, setShowGamificationModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportDateRange, setExportDateRange] = useState({
    preset: 'this_month',
    startDate: '',
    endDate: ''
  });
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showProjectModal, setShowProjectModal] = useState(false);
  // User Management State
  const [usersData, setUsersData] = useState({ users: [], total: 0, page: 1, total_pages: 1 });
  const [userStats, setUserStats] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showUserEditModal, setShowUserEditModal] = useState(false);
  const [showUserMessageModal, setShowUserMessageModal] = useState(false);
  const [showUserPasswordModal, setShowUserPasswordModal] = useState(false);
  const [showUserCertificateModal, setShowUserCertificateModal] = useState(false);
  const [userFilters, setUserFilters] = useState({
    search: '',
    role: 'all',
    status: 'all',
    sortBy: 'newest'
  });
  const [userPage, setUserPage] = useState(1);
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showCourseModal, setShowCourseModal] = useState(false);
  const [showModuleModal, setShowModuleModal] = useState(false);
  const [showLessonModal, setShowLessonModal] = useState(false);
  const [showQuizModal, setShowQuizModal] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [editingModule, setEditingModule] = useState(null);
  const [editingLesson, setEditingLesson] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedModule, setSelectedModule] = useState(null);
  const [expandedCourses, setExpandedCourses] = useState({});
  const [expandedModules, setExpandedModules] = useState({});

  const getToken = () => localStorage.getItem('admin_token');

  const headers = () => ({
    'X-Admin-Token': getToken()
  });

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setIsLoggedIn(false);
    setStats(null);
    setEnrollments([]);
    setContacts([]);
    setSubscribers([]);
    setAttendance([]);
  };

  const fetchData = useCallback(async () => {
    try {
      const token = getToken();
      if (!token) return;
      const hdrs = { 'X-Admin-Token': token };
      const [statsRes, enrollmentsRes, contactsRes, subscribersRes, attendanceRes, coursesRes, teacherAppsRes, teachersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: hdrs }),
        axios.get(`${API}/admin/enrollments`, { headers: hdrs }),
        axios.get(`${API}/admin/contacts`, { headers: hdrs }),
        axios.get(`${API}/admin/newsletter`, { headers: hdrs }),
        axios.get(`${API}/admin/attendance/today`, { headers: hdrs }),
        axios.get(`${API}/admin/courses`, { headers: hdrs }),
        axios.get(`${API}/admin/teacher-applications`, { headers: hdrs }),
        axios.get(`${API}/admin/teachers`, { headers: hdrs })
      ]);
      setStats(statsRes.data);
      setEnrollments(enrollmentsRes.data);
      setContacts(contactsRes.data);
      setSubscribers(subscribersRes.data);
      setAttendance(attendanceRes.data);
      setCourses(coursesRes.data);
      setTeacherApplications(teacherAppsRes.data);
      setTeachers(teachersRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        handleLogout();
        toast.error('Session expired');
      }
    }
  }, []);

  useEffect(() => {
    const token = getToken();
    if (token) {
      setIsLoggedIn(true);
      fetchData();
    }
  }, [fetchData]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem('admin_token', res.data.token);
      setIsLoggedIn(true);
      toast.success('Logged in successfully');
      fetchData();
    } catch (error) {
      toast.error('Invalid password');
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const res = await axios.get(`${API}/admin/leaderboard`, { headers: headers() });
      setLeaderboard(res.data);
    } catch (error) {
      toast.error('Failed to load leaderboard');
    }
  };

  const fetchStudentGamification = async (studentId) => {
    try {
      const res = await axios.get(`${API}/admin/student/${studentId}/gamification`, { headers: headers() });
      setSelectedStudentGamification(res.data);
      setShowGamificationModal(true);
    } catch (error) {
      toast.error('Failed to load student gamification');
    }
  };

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${API}/admin/projects`, { headers: headers() });
      setProjects(res.data);
    } catch (error) {
      toast.error('Failed to load projects');
    }
  };

  // User Management Functions
  const fetchUsers = async (page = 1, filters = userFilters) => {
    setUsersLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
        sort_by: filters.sortBy
      });
      if (filters.search) params.append('search', filters.search);
      if (filters.role !== 'all') params.append('role', filters.role);
      if (filters.status !== 'all') params.append('status', filters.status);
      
      const res = await axios.get(`${API}/admin/users?${params}`, { headers: headers() });
      setUsersData(res.data);
      setUserPage(page);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchUserStats = async () => {
    try {
      const res = await axios.get(`${API}/admin/users/stats`, { headers: headers() });
      setUserStats(res.data);
    } catch (error) {
      console.error('Failed to load user stats');
    }
  };

  const fetchUserDetail = async (userId) => {
    try {
      const res = await axios.get(`${API}/admin/users/${userId}`, { headers: headers() });
      setSelectedUser(res.data);
      setShowUserModal(true);
    } catch (error) {
      toast.error('Failed to load user details');
    }
  };

  const updateUser = async (userId, data) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`, data, { headers: headers() });
      toast.success('User updated successfully');
      setShowUserEditModal(false);
      fetchUsers(userPage);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const archiveUser = async (userId) => {
    if (!window.confirm('Are you sure you want to archive this user? They will no longer be able to login.')) return;
    try {
      await axios.post(`${API}/admin/users/${userId}/archive`, {}, { headers: headers() });
      toast.success('User archived successfully');
      fetchUsers(userPage);
      setShowUserModal(false);
    } catch (error) {
      toast.error('Failed to archive user');
    }
  };

  const activateUser = async (userId) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/activate`, {}, { headers: headers() });
      toast.success('User activated successfully');
      fetchUsers(userPage);
      setShowUserModal(false);
    } catch (error) {
      toast.error('Failed to activate user');
    }
  };

  const resetUserPassword = async (userId, newPassword) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/reset-password`, { new_password: newPassword }, { headers: headers() });
      toast.success('Password reset successfully. User will receive an email.');
      setShowUserPasswordModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const sendUserMessage = async (userId, subject, message) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/message`, { subject, message }, { headers: headers() });
      toast.success('Message sent successfully');
      setShowUserMessageModal(false);
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const issueCertificate = async (userId, courseId, notes) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/certificate`, { course_id: courseId, notes }, { headers: headers() });
      toast.success('Certificate issued successfully');
      setShowUserCertificateModal(false);
      fetchUserDetail(userId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to issue certificate');
    }
  };

  const exportUsers = async (format) => {
    try {
      const params = new URLSearchParams();
      if (userFilters.role !== 'all') params.append('role', userFilters.role);
      if (userFilters.status !== 'all') params.append('status', userFilters.status);
      
      const res = await axios.get(`${API}/admin/users/export/${format}?${params}`, { 
        headers: headers(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `users_export.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Users exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error('Failed to export users');
    }
  };

  const updateProjectStatus = async (projectId, status, isFeatured = null) => {
    try {
      let url = `${API}/admin/projects/${projectId}?status=${status}`;
      if (isFeatured !== null) {
        url += `&is_featured=${isFeatured}`;
      }
      await axios.put(url, {}, { headers: headers() });
      toast.success(`Project ${status}`);
      fetchProjects();
    } catch (error) {
      toast.error('Failed to update project');
    }
  };

  const deleteProject = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    try {
      await axios.delete(`${API}/admin/projects/${projectId}`, { headers: headers() });
      toast.success('Project deleted');
      fetchProjects();
    } catch (error) {
      toast.error('Failed to delete project');
    }
  };

  const getDateRange = (preset) => {
    const today = new Date();
    let startDate, endDate;
    
    switch (preset) {
      case 'today':
        startDate = endDate = today.toISOString().split('T')[0];
        break;
      case 'this_week':
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        startDate = weekStart.toISOString().split('T')[0];
        endDate = today.toISOString().split('T')[0];
        break;
      case 'this_month':
        startDate = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
        endDate = today.toISOString().split('T')[0];
        break;
      case 'last_month':
        startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1).toISOString().split('T')[0];
        endDate = new Date(today.getFullYear(), today.getMonth(), 0).toISOString().split('T')[0];
        break;
      case 'all_time':
        startDate = '2024-01-01';
        endDate = today.toISOString().split('T')[0];
        break;
      default:
        startDate = endDate = today.toISOString().split('T')[0];
    }
    
    return { startDate, endDate };
  };

  const handleExport = async (format) => {
    setExportLoading(true);
    try {
      let startDate, endDate;
      
      if (exportDateRange.preset === 'custom') {
        startDate = exportDateRange.startDate;
        endDate = exportDateRange.endDate;
        if (!startDate || !endDate) {
          toast.error('Please select both start and end dates');
          setExportLoading(false);
          return;
        }
      } else {
        const range = getDateRange(exportDateRange.preset);
        startDate = range.startDate;
        endDate = range.endDate;
      }

      const response = await axios.get(
        `${API}/admin/attendance/export/${format}?start_date=${startDate}&end_date=${endDate}`,
        { 
          headers: headers(),
          responseType: 'blob'
        }
      );

      // Create download link
      const blob = new Blob([response.data], { 
        type: format === 'csv' ? 'text/csv' : 'application/pdf' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `attendance_report_${startDate}_to_${endDate}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(`${format.toUpperCase()} report downloaded successfully!`);
      setShowExportModal(false);
    } catch (error) {
      console.error('Export error:', error);
      if (error.response?.status === 404) {
        toast.error('No attendance records found for the selected period');
      } else {
        toast.error('Failed to export attendance report');
      }
    } finally {
      setExportLoading(false);
    }
  };

  const updateEnrollmentStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/enrollments/${id}`, { status }, { headers: headers() });
      toast.success('Enrollment updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const updateContactStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/contacts/${id}`, { status }, { headers: headers() });
      toast.success('Contact updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const deleteItem = async (type, id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      await axios.delete(`${API}/admin/${type}/${id}`, { headers: headers() });
      toast.success('Deleted successfully');
      fetchData();
      setShowModal(false);
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const signInStudent = async (studentId) => {
    try {
      await axios.post(`${API}/admin/attendance/sign-in`, { student_id: studentId }, { headers: headers() });
      toast.success('Student signed in');
      fetchData();
    } catch (error) {
      toast.error('Failed to sign in student');
    }
  };

  const signOutStudent = async (studentId) => {
    try {
      await axios.post(`${API}/admin/attendance/sign-out`, { student_id: studentId }, { headers: headers() });
      toast.success('Student signed out');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sign out student');
    }
  };

  // Course Management Functions
  const createCourse = async (courseData) => {
    try {
      await axios.post(`${API}/admin/courses`, courseData, { headers: headers() });
      toast.success('Course created successfully');
      fetchData();
      setShowCourseModal(false);
      setEditingCourse(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create course');
    }
  };

  const updateCourse = async (courseId, courseData) => {
    try {
      await axios.put(`${API}/admin/courses/${courseId}`, courseData, { headers: headers() });
      toast.success('Course updated successfully');
      fetchData();
      setShowCourseModal(false);
      setEditingCourse(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update course');
    }
  };

  const deleteCourse = async (courseId) => {
    if (!window.confirm('Are you sure you want to delete this course? This will delete all modules and lessons.')) return;
    try {
      await axios.delete(`${API}/admin/courses/${courseId}`, { headers: headers() });
      toast.success('Course deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete course');
    }
  };

  const addModule = async (courseId, moduleData) => {
    try {
      await axios.post(`${API}/admin/courses/${courseId}/modules`, moduleData, { headers: headers() });
      toast.success('Module added successfully');
      fetchData();
      setShowModuleModal(false);
      setEditingModule(null);
      setSelectedCourse(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add module');
    }
  };

  const updateModule = async (courseId, moduleId, moduleData) => {
    try {
      await axios.put(`${API}/admin/courses/${courseId}/modules/${moduleId}`, moduleData, { headers: headers() });
      toast.success('Module updated successfully');
      fetchData();
      setShowModuleModal(false);
      setEditingModule(null);
      setSelectedCourse(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update module');
    }
  };

  const deleteModule = async (courseId, moduleId) => {
    if (!window.confirm('Are you sure you want to delete this module?')) return;
    try {
      await axios.delete(`${API}/admin/courses/${courseId}/modules/${moduleId}`, { headers: headers() });
      toast.success('Module deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete module');
    }
  };

  const addLesson = async (courseId, moduleId, lessonData) => {
    try {
      await axios.post(`${API}/admin/courses/${courseId}/modules/${moduleId}/lessons`, lessonData, { headers: headers() });
      toast.success('Lesson added successfully');
      fetchData();
      setShowLessonModal(false);
      setEditingLesson(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add lesson');
    }
  };

  const updateLesson = async (courseId, moduleId, lessonId, lessonData) => {
    try {
      await axios.put(`${API}/admin/courses/${courseId}/modules/${moduleId}/lessons/${lessonId}`, lessonData, { headers: headers() });
      toast.success('Lesson updated successfully');
      fetchData();
      setShowLessonModal(false);
      setEditingLesson(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update lesson');
    }
  };

  const deleteLesson = async (courseId, moduleId, lessonId) => {
    if (!window.confirm('Are you sure you want to delete this lesson?')) return;
    try {
      await axios.delete(`${API}/admin/courses/${courseId}/modules/${moduleId}/lessons/${lessonId}`, { headers: headers() });
      toast.success('Lesson deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete lesson');
    }
  };

  const saveQuiz = async (courseId, moduleId, quizData) => {
    try {
      await axios.put(`${API}/admin/courses/${courseId}/modules/${moduleId}/quiz`, quizData, { headers: headers() });
      toast.success('Quiz saved successfully');
      fetchData();
      setShowQuizModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save quiz');
    }
  };

  const deleteQuiz = async (courseId, moduleId) => {
    if (!window.confirm('Are you sure you want to delete this quiz?')) return;
    try {
      await axios.delete(`${API}/admin/courses/${courseId}/modules/${moduleId}/quiz`, { headers: headers() });
      toast.success('Quiz deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete quiz');
    }
  };

  const toggleCourseExpand = (courseId) => {
    setExpandedCourses(prev => ({ ...prev, [courseId]: !prev[courseId] }));
  };

  const toggleModuleExpand = (moduleId) => {
    setExpandedModules(prev => ({ ...prev, [moduleId]: !prev[moduleId] }));
  };

  // Teacher Management Functions
  const approveTeacher = async (applicationId) => {
    try {
      await axios.put(`${API}/admin/teacher-applications/${applicationId}/approve`, {}, { headers: headers() });
      toast.success('Teacher approved successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve teacher');
    }
  };

  const rejectTeacher = async (applicationId) => {
    if (!window.confirm('Are you sure you want to reject this application?')) return;
    try {
      await axios.put(`${API}/admin/teacher-applications/${applicationId}/reject`, {}, { headers: headers() });
      toast.success('Application rejected');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject application');
    }
  };

  const assignCourseToTeacher = async (teacherEmail, courseId) => {
    try {
      await axios.put(`${API}/admin/teachers/${teacherEmail}/assign-course?course_id=${courseId}`, {}, { headers: headers() });
      toast.success('Course assigned to teacher');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign course');
    }
  };

  const removeCourseFromTeacher = async (teacherEmail, courseId) => {
    try {
      await axios.put(`${API}/admin/teachers/${teacherEmail}/remove-course?course_id=${courseId}`, {}, { headers: headers() });
      toast.success('Course removed from teacher');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove course');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Login Screen
  if (!isLoggedIn) {
    return (
      <div className="pt-20 min-h-screen bg-[#060F22] flex items-center justify-center">
        <div className="max-w-md w-full mx-4">
          <div className="bg-[#0A1F44]/50 backdrop-blur-md border border-[#00AEEF]/20 rounded-2xl p-8">
            <h1 className="text-2xl font-bold text-white mb-2 text-center">Admin Dashboard</h1>
            <p className="text-[#94A3B8] text-center mb-8">Enter password to continue</p>
            
            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="admin-password-input"
                  placeholder="Enter admin password"
                  className="bg-[#060F22] border border-[#00AEEF]/30 text-white placeholder-[#94A3B8]/50 focus:border-[#FF6A00] focus:ring-1 focus:ring-[#FF6A00] rounded-xl px-4 py-3 w-full transition-all"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="admin-login-btn"
                className="w-full bg-[#FF6A00] text-white font-bold rounded-xl px-6 py-3 shadow-[0_0_20px_rgba(255,106,0,0.3)] hover:shadow-[0_0_30px_rgba(255,106,0,0.5)] transition-all disabled:opacity-50"
              >
                {loading ? 'Logging in...' : 'Login'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20 min-h-screen bg-[#060F22]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white">Admin Dashboard</h1>
            <p className="text-[#94A3B8]">Manage enrollments, contacts, and subscribers</p>
          </div>
          <button
            onClick={handleLogout}
            data-testid="admin-logout-btn"
            className="flex items-center gap-2 bg-[#0A1F44] text-[#94A3B8] hover:text-white px-4 py-2 rounded-xl border border-[#00AEEF]/20 hover:border-[#FF6A00] transition-all"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <Users className="w-8 h-8 text-[#00AEEF]" />
                <div>
                  <p className="text-2xl font-bold text-white">{stats.total_enrollments}</p>
                  <p className="text-xs text-[#94A3B8]">Total Enrollments</p>
                </div>
              </div>
            </div>
            <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <Clock className="w-8 h-8 text-yellow-400" />
                <div>
                  <p className="text-2xl font-bold text-white">{stats.pending_enrollments}</p>
                  <p className="text-xs text-[#94A3B8]">Pending</p>
                </div>
              </div>
            </div>
            <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-8 h-8 text-green-400" />
                <div>
                  <p className="text-2xl font-bold text-white">{stats.approved_enrollments}</p>
                  <p className="text-xs text-[#94A3B8]">Approved</p>
                </div>
              </div>
            </div>
            <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <Mail className="w-8 h-8 text-[#00FFE0]" />
                <div>
                  <p className="text-2xl font-bold text-white">{stats.total_contacts}</p>
                  <p className="text-xs text-[#94A3B8]">Messages</p>
                </div>
              </div>
            </div>
            <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <Bell className="w-8 h-8 text-[#FF6A00]" />
                <div>
                  <p className="text-2xl font-bold text-white">{stats.unread_contacts}</p>
                  <p className="text-xs text-[#94A3B8]">Unread</p>
                </div>
              </div>
            </div>
            <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <TrendingUp className="w-8 h-8 text-purple-400" />
                <div>
                  <p className="text-2xl font-bold text-white">{stats.total_subscribers}</p>
                  <p className="text-xs text-[#94A3B8]">Subscribers</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-[#00AEEF]/20 overflow-x-auto">
          {[
            { id: 'users', label: 'Users', count: usersData.total || null },
            { id: 'enrollments', label: 'Enrollments', count: enrollments.length },
            { id: 'courses', label: 'Courses', count: courses.length },
            { id: 'teachers', label: 'Teachers', count: teachers.length + teacherApplications.filter(a => a.status === 'pending').length },
            { id: 'projects', label: 'Projects', count: projects.length },
            { id: 'leaderboard', label: 'Leaderboard', count: null },
            { id: 'attendance', label: 'Attendance', count: attendance.filter(a => a.sign_in_time).length },
            { id: 'contacts', label: 'Contacts', count: contacts.length },
            { id: 'subscribers', label: 'Newsletter', count: subscribers.length }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                if (tab.id === 'leaderboard' && !leaderboard) fetchLeaderboard();
                if (tab.id === 'projects' && projects.length === 0) fetchProjects();
                if (tab.id === 'users' && usersData.users.length === 0) {
                  fetchUsers();
                  fetchUserStats();
                }
              }}
              data-testid={`admin-tab-${tab.id}`}
              className={`px-6 py-3 font-medium transition-all whitespace-nowrap ${
                activeTab === tab.id
                  ? 'text-[#00FFE0] border-b-2 border-[#00FFE0]'
                  : 'text-[#94A3B8] hover:text-white'
              }`}
            >
              {tab.label} {tab.count !== null && `(${tab.count})`}
            </button>
          ))}
        </div>

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-6">
            {/* Stats Cards */}
            {userStats && (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-[#00AEEF]/20 rounded-lg">
                      <Users className="w-5 h-5 text-[#00AEEF]" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-white">{userStats.total_users}</p>
                      <p className="text-xs text-[#94A3B8]">Total Users</p>
                    </div>
                  </div>
                </div>
                <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-[#00FFE0]/20 rounded-lg">
                      <GraduationCap className="w-5 h-5 text-[#00FFE0]" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-white">{userStats.total_students}</p>
                      <p className="text-xs text-[#94A3B8]">Students</p>
                    </div>
                  </div>
                </div>
                <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-[#FF6A00]/20 rounded-lg">
                      <BookOpen className="w-5 h-5 text-[#FF6A00]" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-white">{userStats.total_teachers}</p>
                      <p className="text-xs text-[#94A3B8]">Teachers</p>
                    </div>
                  </div>
                </div>
                <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-500/20 rounded-lg">
                      <User className="w-5 h-5 text-purple-400" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-white">{userStats.total_parents}</p>
                      <p className="text-xs text-[#94A3B8]">Parents</p>
                    </div>
                  </div>
                </div>
                <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-500/20 rounded-lg">
                      <Activity className="w-5 h-5 text-green-400" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-white">{userStats.active_today}</p>
                      <p className="text-xs text-[#94A3B8]">Active Today</p>
                    </div>
                  </div>
                </div>
                <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-yellow-500/20 rounded-lg">
                      <Award className="w-5 h-5 text-yellow-400" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-white">{userStats.total_certificates}</p>
                      <p className="text-xs text-[#94A3B8]">Certificates</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Filters & Search */}
            <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-4">
              <div className="flex flex-wrap gap-4 items-center">
                {/* Search */}
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                  <input
                    type="text"
                    placeholder="Search by name or email..."
                    value={userFilters.search}
                    onChange={(e) => setUserFilters({ ...userFilters, search: e.target.value })}
                    onKeyDown={(e) => e.key === 'Enter' && fetchUsers(1, userFilters)}
                    className="w-full pl-10 pr-4 py-2 bg-[#060F22] border border-[#00AEEF]/30 rounded-lg text-white placeholder-[#94A3B8] focus:outline-none focus:border-[#00FFE0]"
                    data-testid="user-search-input"
                  />
                </div>

                {/* Role Filter */}
                <select
                  value={userFilters.role}
                  onChange={(e) => {
                    const newFilters = { ...userFilters, role: e.target.value };
                    setUserFilters(newFilters);
                    fetchUsers(1, newFilters);
                  }}
                  className="px-4 py-2 bg-[#060F22] border border-[#00AEEF]/30 rounded-lg text-white focus:outline-none focus:border-[#00FFE0]"
                  data-testid="user-role-filter"
                >
                  <option value="all">All Roles</option>
                  <option value="student">Students</option>
                  <option value="teacher">Teachers</option>
                  <option value="parent">Parents</option>
                </select>

                {/* Status Filter */}
                <select
                  value={userFilters.status}
                  onChange={(e) => {
                    const newFilters = { ...userFilters, status: e.target.value };
                    setUserFilters(newFilters);
                    fetchUsers(1, newFilters);
                  }}
                  className="px-4 py-2 bg-[#060F22] border border-[#00AEEF]/30 rounded-lg text-white focus:outline-none focus:border-[#00FFE0]"
                  data-testid="user-status-filter"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="archived">Archived</option>
                </select>

                {/* Sort */}
                <select
                  value={userFilters.sortBy}
                  onChange={(e) => {
                    const newFilters = { ...userFilters, sortBy: e.target.value };
                    setUserFilters(newFilters);
                    fetchUsers(1, newFilters);
                  }}
                  className="px-4 py-2 bg-[#060F22] border border-[#00AEEF]/30 rounded-lg text-white focus:outline-none focus:border-[#00FFE0]"
                  data-testid="user-sort-filter"
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                  <option value="name">Name A-Z</option>
                  <option value="progress_high">Highest Progress</option>
                  <option value="progress_low">Lowest Progress</option>
                  <option value="most_active">Most Active</option>
                </select>

                {/* Search Button */}
                <button
                  onClick={() => fetchUsers(1, userFilters)}
                  className="px-4 py-2 bg-[#00AEEF] text-white rounded-lg hover:bg-[#00AEEF]/80 transition-colors flex items-center gap-2"
                  data-testid="user-search-btn"
                >
                  <Search className="w-4 h-4" />
                  Search
                </button>

                {/* Export Dropdown */}
                <div className="relative group">
                  <button className="px-4 py-2 bg-[#FF6A00] text-white rounded-lg hover:bg-[#FF6A00]/80 transition-colors flex items-center gap-2">
                    <Download className="w-4 h-4" />
                    Export
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  <div className="absolute right-0 mt-2 w-40 bg-[#0A1F44] border border-[#00AEEF]/30 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                    <button
                      onClick={() => exportUsers('csv')}
                      className="w-full px-4 py-2 text-left text-white hover:bg-[#00AEEF]/20 flex items-center gap-2"
                      data-testid="export-csv-btn"
                    >
                      <FileSpreadsheet className="w-4 h-4" />
                      Export CSV
                    </button>
                    <button
                      onClick={() => exportUsers('pdf')}
                      className="w-full px-4 py-2 text-left text-white hover:bg-[#00AEEF]/20 flex items-center gap-2"
                      data-testid="export-pdf-btn"
                    >
                      <FileText className="w-4 h-4" />
                      Export PDF
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Users Table */}
            <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl overflow-hidden">
              {usersLoading ? (
                <div className="p-8 text-center">
                  <div className="animate-spin w-8 h-8 border-2 border-[#00AEEF] border-t-transparent rounded-full mx-auto"></div>
                  <p className="text-[#94A3B8] mt-2">Loading users...</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-[#060F22]">
                        <tr>
                          <th className="text-left px-4 py-3 text-xs font-medium text-[#94A3B8] uppercase">User</th>
                          <th className="text-left px-4 py-3 text-xs font-medium text-[#94A3B8] uppercase">Role</th>
                          <th className="text-left px-4 py-3 text-xs font-medium text-[#94A3B8] uppercase">Status</th>
                          <th className="text-left px-4 py-3 text-xs font-medium text-[#94A3B8] uppercase">Progress</th>
                          <th className="text-left px-4 py-3 text-xs font-medium text-[#94A3B8] uppercase">Level</th>
                          <th className="text-left px-4 py-3 text-xs font-medium text-[#94A3B8] uppercase">Points</th>
                          <th className="text-left px-4 py-3 text-xs font-medium text-[#94A3B8] uppercase">Last Active</th>
                          <th className="text-left px-4 py-3 text-xs font-medium text-[#94A3B8] uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#00AEEF]/10">
                        {usersData.users.map((user) => (
                          <tr key={user.id} className="hover:bg-[#00AEEF]/5 transition-colors">
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#00AEEF] to-[#00FFE0] flex items-center justify-center text-white font-bold">
                                  {user.name?.charAt(0)?.toUpperCase() || '?'}
                                </div>
                                <div>
                                  <p className="font-medium text-white">{user.name || 'N/A'}</p>
                                  <p className="text-sm text-[#94A3B8]">{user.email}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                user.role === 'student' ? 'bg-[#00FFE0]/20 text-[#00FFE0]' :
                                user.role === 'teacher' ? 'bg-[#FF6A00]/20 text-[#FF6A00]' :
                                'bg-purple-500/20 text-purple-400'
                              }`}>
                                {user.role?.charAt(0).toUpperCase() + user.role?.slice(1)}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                user.status === 'archived' ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'
                              }`}>
                                {user.status === 'archived' ? 'Archived' : 'Active'}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              {user.role === 'student' && user.stats ? (
                                <div className="flex items-center gap-2">
                                  <div className="w-20 h-2 bg-[#060F22] rounded-full overflow-hidden">
                                    <div 
                                      className="h-full bg-gradient-to-r from-[#00AEEF] to-[#00FFE0] rounded-full"
                                      style={{ width: `${user.stats.overall_progress}%` }}
                                    ></div>
                                  </div>
                                  <span className="text-sm text-white">{user.stats.overall_progress}%</span>
                                </div>
                              ) : (
                                <span className="text-[#94A3B8]">-</span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              {user.stats?.level_name ? (
                                <span className="text-sm font-medium" style={{ color: user.stats.level_color }}>
                                  {user.stats.level_name}
                                </span>
                              ) : (
                                <span className="text-[#94A3B8]">-</span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <span className="text-white font-medium">
                                {user.stats?.total_points?.toLocaleString() || user.total_points?.toLocaleString() || 0}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <span className="text-sm text-[#94A3B8]">
                                {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => fetchUserDetail(user.id)}
                                  className="p-2 text-[#00AEEF] hover:bg-[#00AEEF]/20 rounded-lg transition-colors"
                                  title="View Details"
                                  data-testid={`view-user-${user.id}`}
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => {
                                    setSelectedUser(user);
                                    setShowUserEditModal(true);
                                  }}
                                  className="p-2 text-[#FF6A00] hover:bg-[#FF6A00]/20 rounded-lg transition-colors"
                                  title="Edit User"
                                  data-testid={`edit-user-${user.id}`}
                                >
                                  <Edit2 className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => {
                                    setSelectedUser(user);
                                    setShowUserMessageModal(true);
                                  }}
                                  className="p-2 text-[#00FFE0] hover:bg-[#00FFE0]/20 rounded-lg transition-colors"
                                  title="Send Message"
                                  data-testid={`message-user-${user.id}`}
                                >
                                  <MessageSquare className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {usersData.total_pages > 1 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t border-[#00AEEF]/10">
                      <p className="text-sm text-[#94A3B8]">
                        Showing {(userPage - 1) * 20 + 1} to {Math.min(userPage * 20, usersData.total)} of {usersData.total} users
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => fetchUsers(userPage - 1)}
                          disabled={userPage === 1}
                          className="px-3 py-1 bg-[#060F22] text-white rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#00AEEF]/20"
                        >
                          Previous
                        </button>
                        <span className="px-3 py-1 text-white">
                          Page {userPage} of {usersData.total_pages}
                        </span>
                        <button
                          onClick={() => fetchUsers(userPage + 1)}
                          disabled={userPage === usersData.total_pages}
                          className="px-3 py-1 bg-[#060F22] text-white rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#00AEEF]/20"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* Enrollments Tab */}
        {activeTab === 'enrollments' && (
          <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#060F22]">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-medium text-[#94A3B8] uppercase">Student</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-[#94A3B8] uppercase">Program</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-[#94A3B8] uppercase">Parent</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-[#94A3B8] uppercase">Status</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-[#94A3B8] uppercase">Date</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-[#94A3B8] uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#00AEEF]/10">
                  {enrollments.map((e) => (
                    <tr key={e.id} className="hover:bg-[#060F22]/50">
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-white font-medium">{e.student_first_name} {e.student_last_name}</p>
                          <p className="text-[#94A3B8] text-sm">{e.student_email}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-[#94A3B8]">{e.program}</td>
                      <td className="px-4 py-3">
                        <p className="text-white">{e.parent_first_name} {e.parent_last_name}</p>
                        <p className="text-[#94A3B8] text-sm">{e.parent_phone}</p>
                      </td>
                      <td className="px-4 py-3"><StatusBadge status={e.status} /></td>
                      <td className="px-4 py-3 text-[#94A3B8] text-sm">{formatDate(e.created_at)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => { setSelectedItem(e); setShowModal(true); }}
                            className="p-2 text-[#00AEEF] hover:bg-[#00AEEF]/20 rounded-lg"
                            data-testid={`view-enrollment-${e.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <select
                            value={e.status || 'pending'}
                            onChange={(ev) => updateEnrollmentStatus(e.id, ev.target.value)}
                            className="bg-[#060F22] border border-[#00AEEF]/30 text-white rounded-lg px-2 py-1 text-sm"
                          >
                            <option value="pending">Pending</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Rejected</option>
                          </select>
                          <button
                            onClick={() => deleteItem('enrollments', e.id)}
                            className="p-2 text-red-400 hover:bg-red-400/20 rounded-lg"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {enrollments.length === 0 && (
                    <tr>
                      <td colSpan="6" className="px-4 py-8 text-center text-[#94A3B8]">
                        No enrollments yet
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Courses Tab */}
        {activeTab === 'courses' && (
          <div className="space-y-6">
            {/* Header with Add Course Button */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <BookOpen className="w-6 h-6 text-[#00AEEF]" />
                <h2 className="text-xl font-bold text-white">Course Management</h2>
              </div>
              <button
                onClick={() => { setEditingCourse(null); setShowCourseModal(true); }}
                data-testid="add-course-btn"
                className="flex items-center gap-2 bg-[#FF6A00] text-white px-4 py-2 rounded-xl hover:shadow-[0_0_20px_rgba(255,106,0,0.3)] transition-all"
              >
                <Plus className="w-4 h-4" />
                Add Course
              </button>
            </div>

            {/* Courses List */}
            {courses.length === 0 ? (
              <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-12 text-center">
                <GraduationCap className="w-16 h-16 text-[#94A3B8] mx-auto mb-4" />
                <h3 className="text-white text-lg font-medium mb-2">No Courses Yet</h3>
                <p className="text-[#94A3B8] mb-4">Create your first course to get started</p>
                <button
                  onClick={() => { setEditingCourse(null); setShowCourseModal(true); }}
                  className="inline-flex items-center gap-2 bg-[#00AEEF] text-white px-4 py-2 rounded-xl hover:bg-[#00AEEF]/80 transition-all"
                >
                  <Plus className="w-4 h-4" />
                  Create Course
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {courses.map((course) => (
                  <div key={course.id} className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl overflow-hidden">
                    {/* Course Header */}
                    <div 
                      className="p-4 flex items-center justify-between cursor-pointer hover:bg-[#060F22]/30"
                      onClick={() => toggleCourseExpand(course.id)}
                    >
                      <div className="flex items-center gap-4">
                        <ChevronRight className={`w-5 h-5 text-[#94A3B8] transition-transform ${expandedCourses[course.id] ? 'rotate-90' : ''}`} />
                        <div>
                          <h3 className="text-white font-bold text-lg">{course.title}</h3>
                          <p className="text-[#94A3B8] text-sm">{course.description?.substring(0, 100)}...</p>
                          <div className="flex items-center gap-4 mt-2">
                            <span className="text-xs text-[#00AEEF]">{course.duration}</span>
                            <span className="text-xs text-[#94A3B8]">{course.modules?.length || 0} modules</span>
                            <span className="text-xs text-[#94A3B8]">
                              {course.modules?.reduce((sum, m) => sum + (m.lessons?.length || 0), 0)} lessons
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => { setEditingCourse(course); setShowCourseModal(true); }}
                          className="p-2 text-[#00AEEF] hover:bg-[#00AEEF]/20 rounded-lg"
                          data-testid={`edit-course-${course.id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => deleteCourse(course.id)}
                          className="p-2 text-red-400 hover:bg-red-400/20 rounded-lg"
                          data-testid={`delete-course-${course.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {/* Expanded Course Content - Modules */}
                    {expandedCourses[course.id] && (
                      <div className="border-t border-[#00AEEF]/10 p-4 bg-[#060F22]/30">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-white font-medium">Modules</h4>
                          <button
                            onClick={() => { setSelectedCourse(course); setEditingModule(null); setShowModuleModal(true); }}
                            className="flex items-center gap-1 text-sm text-[#00FFE0] hover:text-[#00FFE0]/80"
                            data-testid={`add-module-${course.id}`}
                          >
                            <Plus className="w-4 h-4" />
                            Add Module
                          </button>
                        </div>

                        {course.modules?.length === 0 ? (
                          <p className="text-[#94A3B8] text-sm text-center py-4">No modules yet. Add your first module.</p>
                        ) : (
                          <div className="space-y-3">
                            {course.modules?.sort((a, b) => a.order - b.order).map((module) => (
                              <div key={module.id} className="bg-[#0A1F44]/50 rounded-xl overflow-hidden">
                                {/* Module Header */}
                                <div 
                                  className="p-3 flex items-center justify-between cursor-pointer hover:bg-[#060F22]/30"
                                  onClick={() => toggleModuleExpand(module.id)}
                                >
                                  <div className="flex items-center gap-3">
                                    <ChevronRight className={`w-4 h-4 text-[#94A3B8] transition-transform ${expandedModules[module.id] ? 'rotate-90' : ''}`} />
                                    <div>
                                      <p className="text-white font-medium">{module.title}</p>
                                      <div className="flex items-center gap-3 text-xs text-[#94A3B8]">
                                        <span>{module.lessons?.length || 0} lessons</span>
                                        {module.quiz && <span className="text-[#FF6A00]">Has Quiz</span>}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                    <button
                                      onClick={() => { setSelectedCourse(course); setEditingModule(module); setShowModuleModal(true); }}
                                      className="p-1.5 text-[#00AEEF] hover:bg-[#00AEEF]/20 rounded-lg"
                                    >
                                      <Edit2 className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                      onClick={() => deleteModule(course.id, module.id)}
                                      className="p-1.5 text-red-400 hover:bg-red-400/20 rounded-lg"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                </div>

                                {/* Expanded Module Content - Lessons & Quiz */}
                                {expandedModules[module.id] && (
                                  <div className="border-t border-[#00AEEF]/10 p-3 bg-[#060F22]/20">
                                    {/* Lessons */}
                                    <div className="mb-4">
                                      <div className="flex items-center justify-between mb-2">
                                        <h5 className="text-sm text-[#94A3B8] font-medium">Lessons</h5>
                                        <button
                                          onClick={() => { setSelectedCourse(course); setSelectedModule(module); setEditingLesson(null); setShowLessonModal(true); }}
                                          className="flex items-center gap-1 text-xs text-[#00FFE0] hover:text-[#00FFE0]/80"
                                        >
                                          <Plus className="w-3 h-3" />
                                          Add Lesson
                                        </button>
                                      </div>
                                      {module.lessons?.length === 0 ? (
                                        <p className="text-[#94A3B8] text-xs text-center py-2">No lessons yet</p>
                                      ) : (
                                        <div className="space-y-1">
                                          {module.lessons?.sort((a, b) => a.order - b.order).map((lesson) => (
                                            <div key={lesson.id} className="flex items-center justify-between p-2 bg-[#0A1F44]/30 rounded-lg">
                                              <div className="flex items-center gap-2">
                                                {lesson.type === 'video' && <Video className="w-3.5 h-3.5 text-[#00AEEF]" />}
                                                {lesson.type === 'reading' && <FileText className="w-3.5 h-3.5 text-[#00FFE0]" />}
                                                {lesson.type === 'assignment' && <ClipboardList className="w-3.5 h-3.5 text-[#FF6A00]" />}
                                                <span className="text-white text-sm">{lesson.title}</span>
                                                <span className="text-[#94A3B8] text-xs">({lesson.duration})</span>
                                              </div>
                                              <div className="flex items-center gap-1">
                                                <button
                                                  onClick={() => { setSelectedCourse(course); setSelectedModule(module); setEditingLesson(lesson); setShowLessonModal(true); }}
                                                  className="p-1 text-[#00AEEF] hover:bg-[#00AEEF]/20 rounded"
                                                >
                                                  <Edit2 className="w-3 h-3" />
                                                </button>
                                                <button
                                                  onClick={() => deleteLesson(course.id, module.id, lesson.id)}
                                                  className="p-1 text-red-400 hover:bg-red-400/20 rounded"
                                                >
                                                  <Trash2 className="w-3 h-3" />
                                                </button>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>

                                    {/* Quiz */}
                                    <div>
                                      <div className="flex items-center justify-between mb-2">
                                        <h5 className="text-sm text-[#94A3B8] font-medium">Quiz</h5>
                                        {!module.quiz && (
                                          <button
                                            onClick={() => { setSelectedCourse(course); setSelectedModule(module); setShowQuizModal(true); }}
                                            className="flex items-center gap-1 text-xs text-[#FF6A00] hover:text-[#FF6A00]/80"
                                          >
                                            <Plus className="w-3 h-3" />
                                            Add Quiz
                                          </button>
                                        )}
                                      </div>
                                      {module.quiz ? (
                                        <div className="p-2 bg-[#FF6A00]/10 border border-[#FF6A00]/20 rounded-lg flex items-center justify-between">
                                          <div>
                                            <p className="text-white text-sm font-medium">{module.quiz.title}</p>
                                            <p className="text-[#94A3B8] text-xs">{module.quiz.questions?.length || 0} questions</p>
                                          </div>
                                          <div className="flex items-center gap-1">
                                            <button
                                              onClick={() => { setSelectedCourse(course); setSelectedModule(module); setShowQuizModal(true); }}
                                              className="p-1.5 text-[#FF6A00] hover:bg-[#FF6A00]/20 rounded"
                                            >
                                              <Edit2 className="w-3.5 h-3.5" />
                                            </button>
                                            <button
                                              onClick={() => deleteQuiz(course.id, module.id)}
                                              className="p-1.5 text-red-400 hover:bg-red-400/20 rounded"
                                            >
                                              <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                          </div>
                                        </div>
                                      ) : (
                                        <p className="text-[#94A3B8] text-xs text-center py-2">No quiz for this module</p>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Teachers Tab */}
        {activeTab === 'teachers' && (
          <div className="space-y-6">
            {/* Pending Applications */}
            {teacherApplications.filter(a => a.status === 'pending').length > 0 && (
              <div className="bg-[#0A1F44]/50 border border-[#FF6A00]/30 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-3 h-3 bg-[#FF6A00] rounded-full animate-pulse"></div>
                  <h2 className="text-xl font-bold text-white">Pending Applications ({teacherApplications.filter(a => a.status === 'pending').length})</h2>
                </div>
                <div className="space-y-4">
                  {teacherApplications.filter(a => a.status === 'pending').map((app) => (
                    <div key={app.id} className="bg-[#060F22]/50 rounded-xl p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="text-white font-bold text-lg">{app.name}</h3>
                          <p className="text-[#94A3B8] text-sm">{app.email}</p>
                          <div className="flex flex-wrap gap-4 mt-2 text-sm">
                            <span className="text-[#00AEEF]">{app.specialization}</span>
                            <span className="text-[#94A3B8]">{app.experience_years} years exp.</span>
                            <span className="text-[#94A3B8]">{app.phone}</span>
                          </div>
                          <p className="text-[#94A3B8] text-sm mt-2"><strong>Qualifications:</strong> {app.qualifications}</p>
                          <p className="text-[#94A3B8] text-sm mt-1"><strong>Bio:</strong> {app.bio}</p>
                        </div>
                        <div className="flex gap-2 ml-4">
                          <button
                            onClick={() => approveTeacher(app.id)}
                            data-testid={`approve-teacher-${app.id}`}
                            className="flex items-center gap-1 px-4 py-2 bg-green-500/20 text-green-400 rounded-xl hover:bg-green-500/30 transition-all"
                          >
                            <CheckCircle className="w-4 h-4" />
                            Approve
                          </button>
                          <button
                            onClick={() => rejectTeacher(app.id)}
                            className="flex items-center gap-1 px-4 py-2 bg-red-500/20 text-red-400 rounded-xl hover:bg-red-500/30 transition-all"
                          >
                            <X className="w-4 h-4" />
                            Reject
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Approved Teachers */}
            <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <GraduationCap className="w-6 h-6 text-[#00AEEF]" />
                <h2 className="text-xl font-bold text-white">Active Teachers ({teachers.length})</h2>
              </div>
              {teachers.length === 0 ? (
                <div className="text-center py-8">
                  <GraduationCap className="w-12 h-12 text-[#94A3B8] mx-auto mb-4" />
                  <p className="text-[#94A3B8]">No approved teachers yet</p>
                  <p className="text-[#94A3B8] text-sm">Teachers can apply at /teacher/register</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {teachers.map((teacher) => (
                    <div key={teacher.email} className="bg-[#060F22]/50 rounded-xl p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="text-white font-bold">{teacher.name}</h3>
                          <p className="text-[#94A3B8] text-sm">{teacher.email}</p>
                          <div className="flex flex-wrap gap-2 mt-2">
                            <span className="px-2 py-1 bg-[#00AEEF]/20 text-[#00AEEF] text-xs rounded-full">{teacher.specialization}</span>
                            <span className="px-2 py-1 bg-[#060F22] text-[#94A3B8] text-xs rounded-full">{teacher.experience_years} years</span>
                          </div>
                          {/* Assigned Courses */}
                          <div className="mt-3">
                            <p className="text-xs text-[#94A3B8] mb-2">Assigned Courses:</p>
                            <div className="flex flex-wrap gap-2">
                              {teacher.assigned_courses?.length > 0 ? (
                                teacher.assigned_courses.map((courseId) => {
                                  const course = courses.find(c => c.id === courseId);
                                  return course ? (
                                    <span key={courseId} className="px-2 py-1 bg-[#00FFE0]/20 text-[#00FFE0] text-xs rounded-full flex items-center gap-1">
                                      {course.title}
                                      <button onClick={() => removeCourseFromTeacher(teacher.email, courseId)} className="text-red-400 hover:text-red-300 ml-1">×</button>
                                    </span>
                                  ) : null;
                                })
                              ) : (
                                <span className="text-[#94A3B8] text-xs">None assigned</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div>
                          <select
                            onChange={(e) => { if (e.target.value) assignCourseToTeacher(teacher.email, e.target.value); e.target.value = ''; }}
                            className="bg-[#060F22] border border-[#00AEEF]/30 text-white rounded-xl px-3 py-2 text-sm"
                            defaultValue=""
                          >
                            <option value="" disabled>Assign Course</option>
                            {courses.filter(c => !teacher.assigned_courses?.includes(c.id)).map((course) => (
                              <option key={course.id} value={course.id}>{course.title}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Projects Tab */}
        {activeTab === 'projects' && (
          <div className="space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-6">
                <p className="text-2xl font-bold text-white">{projects.filter(p => p.status === 'pending').length}</p>
                <p className="text-sm text-[#94A3B8]">Pending Review</p>
              </div>
              <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-6">
                <p className="text-2xl font-bold text-[#00FFE0]">{projects.filter(p => p.status === 'approved').length}</p>
                <p className="text-sm text-[#94A3B8]">Approved</p>
              </div>
              <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-6">
                <p className="text-2xl font-bold text-[#00AEEF]">{projects.filter(p => p.status === 'in_progress').length}</p>
                <p className="text-sm text-[#94A3B8]">In Progress</p>
              </div>
              <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-6">
                <p className="text-2xl font-bold text-[#FFD700]">{projects.filter(p => p.is_donor).length}</p>
                <p className="text-sm text-[#94A3B8]">Donor Projects</p>
              </div>
            </div>

            {/* Projects Table */}
            <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl overflow-hidden">
              <div className="p-4 border-b border-[#00AEEF]/10">
                <h2 className="text-lg font-bold text-white flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-[#00AEEF]" />
                  Project Submissions
                </h2>
              </div>
              {projects.length > 0 ? (
                <div className="divide-y divide-[#00AEEF]/10">
                  {projects.map((project) => (
                    <div key={project.id} className="p-4 hover:bg-[#060F22]/50">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            {project.is_donor && (
                              <span className="px-2 py-0.5 bg-[#FFD700]/20 text-[#FFD700] text-xs rounded-full">Donor</span>
                            )}
                            <span className={`px-2 py-0.5 text-xs rounded-full ${
                              project.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                              project.status === 'approved' ? 'bg-green-500/20 text-green-400' :
                              project.status === 'in_progress' ? 'bg-blue-500/20 text-blue-400' :
                              project.status === 'completed' ? 'bg-[#00FFE0]/20 text-[#00FFE0]' :
                              'bg-red-500/20 text-red-400'
                            }`}>
                              {project.status}
                            </span>
                          </div>
                          <h3 className="text-white font-bold">{project.project_title}</h3>
                          <p className="text-[#94A3B8] text-sm">{project.business_name} - {project.contact_email}</p>
                          <p className="text-[#94A3B8] text-sm mt-1 line-clamp-2">{project.project_description}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-[#94A3B8]">
                            <span>Type: {project.project_type}</span>
                            <span>Timeline: {project.timeline}</span>
                            {project.applications?.length > 0 && (
                              <span className="text-[#00AEEF]">{project.applications.length} applications</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          {project.status === 'pending' && (
                            <>
                              <button
                                onClick={() => updateProjectStatus(project.id, 'approved')}
                                className="px-3 py-1 bg-green-500/20 text-green-400 rounded-lg text-sm hover:bg-green-500/30"
                              >
                                Approve
                              </button>
                              <button
                                onClick={() => updateProjectStatus(project.id, 'rejected')}
                                className="px-3 py-1 bg-red-500/20 text-red-400 rounded-lg text-sm hover:bg-red-500/30"
                              >
                                Reject
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => { setSelectedProject(project); setShowProjectModal(true); }}
                            className="px-3 py-1 bg-[#00AEEF]/20 text-[#00AEEF] rounded-lg text-sm hover:bg-[#00AEEF]/30"
                          >
                            View
                          </button>
                          <button
                            onClick={() => deleteProject(project.id)}
                            className="px-3 py-1 bg-red-500/20 text-red-400 rounded-lg text-sm hover:bg-red-500/30"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center">
                  <Briefcase className="w-12 h-12 text-[#94A3B8] mx-auto mb-4" />
                  <p className="text-[#94A3B8]">No project submissions yet</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Leaderboard Tab */}
        {activeTab === 'leaderboard' && (
          <div className="space-y-6">
            {/* Stats Cards */}
            {leaderboard && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#00AEEF]/20 flex items-center justify-center">
                      <Users className="w-5 h-5 text-[#00AEEF]" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-white">{leaderboard.stats?.total_students || 0}</p>
                      <p className="text-sm text-[#94A3B8]">Total Students</p>
                    </div>
                  </div>
                </div>
                <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#00FFE0]/20 flex items-center justify-center">
                      <Zap className="w-5 h-5 text-[#00FFE0]" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-white">{leaderboard.stats?.students_with_points || 0}</p>
                      <p className="text-sm text-[#94A3B8]">Active Learners</p>
                    </div>
                  </div>
                </div>
                <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#FF6A00]/20 flex items-center justify-center">
                      <Star className="w-5 h-5 text-[#FF6A00]" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-white">{leaderboard.stats?.total_points_awarded?.toLocaleString() || 0}</p>
                      <p className="text-sm text-[#94A3B8]">Total Points Awarded</p>
                    </div>
                  </div>
                </div>
                <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#FFD700]/20 flex items-center justify-center">
                      <Medal className="w-5 h-5 text-[#FFD700]" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-white">{leaderboard.stats?.total_badges_earned || 0}</p>
                      <p className="text-sm text-[#94A3B8]">Badges Earned</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Engagement Rate */}
            {leaderboard && leaderboard.stats && (
              <div className="bg-gradient-to-r from-[#FF6A00]/10 to-[#FFD700]/10 border border-[#FF6A00]/30 rounded-xl p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-bold text-white">Student Engagement Rate</h3>
                    <p className="text-[#94A3B8]">Percentage of students actively earning points</p>
                  </div>
                  <div className="text-right">
                    <p className="text-4xl font-black text-[#FF6A00]">{leaderboard.stats.engagement_rate}%</p>
                    <p className="text-sm text-[#94A3B8]">{leaderboard.stats.students_with_points} of {leaderboard.stats.total_students} students</p>
                  </div>
                </div>
              </div>
            )}

            {/* Leaderboard Table */}
            <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl overflow-hidden">
              <div className="p-4 border-b border-[#00AEEF]/10">
                <h2 className="text-lg font-bold text-white flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-[#FFD700]" />
                  Student Leaderboard
                </h2>
              </div>
              {leaderboard && leaderboard.leaderboard.length > 0 ? (
                <div className="divide-y divide-[#00AEEF]/10">
                  {leaderboard.leaderboard.map((entry) => (
                    <div 
                      key={entry.student_id}
                      className={`flex items-center gap-4 p-4 hover:bg-[#060F22]/50 cursor-pointer ${
                        entry.rank <= 3 ? 'bg-gradient-to-r from-[#FFD700]/5 to-transparent' : ''
                      }`}
                      onClick={() => fetchStudentGamification(entry.student_id)}
                      data-testid={`leaderboard-row-${entry.student_id}`}
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                        entry.rank === 1 ? 'bg-[#FFD700]/30 text-[#FFD700]' :
                        entry.rank === 2 ? 'bg-gray-400/30 text-gray-300' :
                        entry.rank === 3 ? 'bg-amber-600/30 text-amber-500' :
                        'bg-[#0A1F44] text-[#94A3B8]'
                      }`}>
                        {entry.rank <= 3 ? <Trophy className="w-5 h-5" /> : entry.rank}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-white">{entry.name}</p>
                        <p className="text-xs text-[#94A3B8]">{entry.email}</p>
                      </div>
                      <div className="text-center">
                        <p className="font-medium" style={{ color: entry.level?.color || '#00FFE0' }}>
                          {entry.level?.name || 'Beginner'}
                        </p>
                        <p className="text-xs text-[#94A3B8]">Level {entry.level?.level || 1}</p>
                      </div>
                      <div className="text-center">
                        <div className="flex items-center gap-1 justify-center">
                          <Medal className="w-4 h-4 text-[#FFD700]" />
                          <span className="text-white font-medium">{entry.badges_count}</span>
                        </div>
                        <p className="text-xs text-[#94A3B8]">Badges</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-white">{entry.total_points.toLocaleString()}</p>
                        <p className="text-xs text-[#94A3B8]">points</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center">
                  <Trophy className="w-12 h-12 text-[#94A3B8] mx-auto mb-4" />
                  <p className="text-[#94A3B8]">No students have earned points yet</p>
                  <p className="text-sm text-[#94A3B8]/70 mt-2">Points are awarded when students complete lessons, quizzes, and assignments</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Attendance Tab */}
        {activeTab === 'attendance' && (
          <div className="space-y-6">
            <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Calendar className="w-6 h-6 text-[#00AEEF]" />
                  <h2 className="text-xl font-bold text-white">Today&apos;s Attendance</h2>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-[#94A3B8]">{new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
                  <button
                    onClick={() => setShowExportModal(true)}
                    data-testid="export-attendance-btn"
                    className="flex items-center gap-2 bg-[#00AEEF] text-white px-4 py-2 rounded-xl hover:bg-[#00AEEF]/80 transition-all"
                  >
                    <Download className="w-4 h-4" />
                    Export
                  </button>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-[#060F22]/50 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-[#00FFE0]">{attendance.filter(a => a.sign_in_time).length}</p>
                  <p className="text-sm text-[#94A3B8]">Present</p>
                </div>
                <div className="bg-[#060F22]/50 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-yellow-400">{attendance.filter(a => a.sign_in_time && !a.sign_out_time).length}</p>
                  <p className="text-sm text-[#94A3B8]">Signed In</p>
                </div>
                <div className="bg-[#060F22]/50 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-red-400">{attendance.filter(a => !a.sign_in_time).length}</p>
                  <p className="text-sm text-[#94A3B8]">Absent</p>
                </div>
              </div>
            </div>

            <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#060F22]">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs font-medium text-[#94A3B8] uppercase">Student</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-[#94A3B8] uppercase">Sign In</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-[#94A3B8] uppercase">Sign Out</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-[#94A3B8] uppercase">Status</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-[#94A3B8] uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#00AEEF]/10">
                    {attendance.map((a, idx) => (
                      <tr key={idx} className="hover:bg-[#060F22]/50">
                        <td className="px-4 py-3">
                          <p className="text-white font-medium">{a.student_name}</p>
                          <p className="text-[#94A3B8] text-sm">{a.student_email}</p>
                        </td>
                        <td className="px-4 py-3">
                          {a.sign_in_time ? (
                            <span className="text-[#00FFE0] font-medium">{a.sign_in_time}</span>
                          ) : (
                            <span className="text-[#94A3B8]">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {a.sign_out_time ? (
                            <span className="text-[#FF6A00] font-medium">{a.sign_out_time}</span>
                          ) : (
                            <span className="text-[#94A3B8]">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            a.status === 'present' ? 'bg-green-500/20 text-green-400' :
                            a.status === 'late' ? 'bg-yellow-500/20 text-yellow-400' :
                            'bg-red-500/20 text-red-400'
                          }`}>
                            {a.status?.charAt(0).toUpperCase() + a.status?.slice(1)}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {!a.sign_in_time ? (
                              <button
                                onClick={() => signInStudent(a.student_id)}
                                data-testid={`sign-in-${a.student_id}`}
                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#00FFE0]/20 text-[#00FFE0] rounded-lg hover:bg-[#00FFE0]/30 transition-colors text-sm font-medium"
                              >
                                <UserCheck className="w-4 h-4" />
                                Sign In
                              </button>
                            ) : !a.sign_out_time ? (
                              <button
                                onClick={() => signOutStudent(a.student_id)}
                                data-testid={`sign-out-${a.student_id}`}
                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#FF6A00]/20 text-[#FF6A00] rounded-lg hover:bg-[#FF6A00]/30 transition-colors text-sm font-medium"
                              >
                                <UserX className="w-4 h-4" />
                                Sign Out
                              </button>
                            ) : (
                              <span className="text-[#94A3B8] text-sm">Completed</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {attendance.length === 0 && (
                      <tr>
                        <td colSpan="5" className="px-4 py-8 text-center text-[#94A3B8]">
                          No students enrolled yet
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Contacts Tab */}
        {activeTab === 'contacts' && (
          <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#060F22]">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-medium text-[#94A3B8] uppercase">From</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-[#94A3B8] uppercase">Subject</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-[#94A3B8] uppercase">Status</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-[#94A3B8] uppercase">Date</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-[#94A3B8] uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#00AEEF]/10">
                  {contacts.map((c) => (
                    <tr key={c.id} className="hover:bg-[#060F22]/50">
                      <td className="px-4 py-3">
                        <p className="text-white font-medium">{c.name}</p>
                        <p className="text-[#94A3B8] text-sm">{c.email}</p>
                      </td>
                      <td className="px-4 py-3 text-[#94A3B8]">{c.subject}</td>
                      <td className="px-4 py-3"><StatusBadge status={c.status || 'unread'} /></td>
                      <td className="px-4 py-3 text-[#94A3B8] text-sm">{formatDate(c.created_at)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => { setSelectedItem(c); setShowModal(true); updateContactStatus(c.id, 'read'); }}
                            className="p-2 text-[#00AEEF] hover:bg-[#00AEEF]/20 rounded-lg"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <select
                            value={c.status || 'unread'}
                            onChange={(ev) => updateContactStatus(c.id, ev.target.value)}
                            className="bg-[#060F22] border border-[#00AEEF]/30 text-white rounded-lg px-2 py-1 text-sm"
                          >
                            <option value="unread">Unread</option>
                            <option value="read">Read</option>
                            <option value="replied">Replied</option>
                          </select>
                          <button
                            onClick={() => deleteItem('contacts', c.id)}
                            className="p-2 text-red-400 hover:bg-red-400/20 rounded-lg"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {contacts.length === 0 && (
                    <tr>
                      <td colSpan="5" className="px-4 py-8 text-center text-[#94A3B8]">
                        No contact messages yet
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Newsletter Tab */}
        {activeTab === 'subscribers' && (
          <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#060F22]">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-medium text-[#94A3B8] uppercase">Email</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-[#94A3B8] uppercase">Subscribed</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-[#94A3B8] uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#00AEEF]/10">
                  {subscribers.map((s) => (
                    <tr key={s.id} className="hover:bg-[#060F22]/50">
                      <td className="px-4 py-3 text-white">{s.email}</td>
                      <td className="px-4 py-3 text-[#94A3B8] text-sm">{formatDate(s.created_at)}</td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => deleteItem('newsletter', s.id)}
                          className="p-2 text-red-400 hover:bg-red-400/20 rounded-lg"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {subscribers.length === 0 && (
                    <tr>
                      <td colSpan="3" className="px-4 py-8 text-center text-[#94A3B8]">
                        No subscribers yet
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Detail Modal */}
        {showModal && selectedItem && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-[#0A1F44] border border-[#00AEEF]/20 rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between p-6 border-b border-[#00AEEF]/20">
                <h2 className="text-xl font-bold text-white">
                  {selectedItem.student_first_name ? 'Enrollment Details' : 'Contact Message'}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 text-[#94A3B8] hover:text-white rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                {selectedItem.student_first_name ? (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-[#94A3B8] uppercase mb-1">Student Name</p>
                        <p className="text-white">{selectedItem.student_first_name} {selectedItem.student_last_name}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[#94A3B8] uppercase mb-1">Student Email</p>
                        <p className="text-white">{selectedItem.student_email}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[#94A3B8] uppercase mb-1">Age</p>
                        <p className="text-white">{selectedItem.student_age}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[#94A3B8] uppercase mb-1">Grade</p>
                        <p className="text-white">{selectedItem.student_grade}</p>
                      </div>
                    </div>
                    <hr className="border-[#00AEEF]/20" />
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-[#94A3B8] uppercase mb-1">Parent/Guardian</p>
                        <p className="text-white">{selectedItem.parent_first_name} {selectedItem.parent_last_name}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[#94A3B8] uppercase mb-1">Parent Email</p>
                        <p className="text-white">{selectedItem.parent_email}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[#94A3B8] uppercase mb-1">Parent Phone</p>
                        <p className="text-white">{selectedItem.parent_phone}</p>
                      </div>
                    </div>
                    <hr className="border-[#00AEEF]/20" />
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-[#94A3B8] uppercase mb-1">Program</p>
                        <p className="text-white">{selectedItem.program}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[#94A3B8] uppercase mb-1">Experience Level</p>
                        <p className="text-white">{selectedItem.experience_level}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[#94A3B8] uppercase mb-1">How They Heard</p>
                        <p className="text-white">{selectedItem.how_heard}</p>
                      </div>
                    </div>
                    {selectedItem.additional_info && (
                      <div>
                        <p className="text-xs text-[#94A3B8] uppercase mb-1">Additional Info</p>
                        <p className="text-white bg-[#060F22] p-4 rounded-xl">{selectedItem.additional_info}</p>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-[#94A3B8] uppercase mb-1">Name</p>
                        <p className="text-white">{selectedItem.name}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[#94A3B8] uppercase mb-1">Email</p>
                        <p className="text-white">{selectedItem.email}</p>
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-[#94A3B8] uppercase mb-1">Subject</p>
                      <p className="text-white">{selectedItem.subject}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[#94A3B8] uppercase mb-1">Message</p>
                      <p className="text-white bg-[#060F22] p-4 rounded-xl whitespace-pre-wrap">{selectedItem.message}</p>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Course Modal */}
        <CourseModal
          show={showCourseModal}
          course={editingCourse}
          onClose={() => { setShowCourseModal(false); setEditingCourse(null); }}
          onSave={(data) => editingCourse ? updateCourse(editingCourse.id, data) : createCourse(data)}
        />

        {/* Module Modal */}
        <ModuleModal
          show={showModuleModal}
          module={editingModule}
          course={selectedCourse}
          onClose={() => { setShowModuleModal(false); setEditingModule(null); setSelectedCourse(null); }}
          onSave={(data) => editingModule ? updateModule(selectedCourse.id, editingModule.id, data) : addModule(selectedCourse.id, data)}
        />

        {/* Lesson Modal */}
        <LessonModal
          show={showLessonModal}
          lesson={editingLesson}
          course={selectedCourse}
          module={selectedModule}
          onClose={() => { setShowLessonModal(false); setEditingLesson(null); setSelectedCourse(null); setSelectedModule(null); }}
          onSave={(data) => editingLesson ? updateLesson(selectedCourse.id, selectedModule.id, editingLesson.id, data) : addLesson(selectedCourse.id, selectedModule.id, data)}
        />

        {/* Project Details Modal */}
        {showProjectModal && selectedProject && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-[#0A1F44] border border-[#00AEEF]/20 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-6 border-b border-[#00AEEF]/20">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    {selectedProject.is_donor && (
                      <span className="px-2 py-0.5 bg-[#FFD700]/20 text-[#FFD700] text-xs rounded-full">Donor Project</span>
                    )}
                    <span className={`px-2 py-0.5 text-xs rounded-full ${
                      selectedProject.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                      selectedProject.status === 'approved' ? 'bg-green-500/20 text-green-400' :
                      selectedProject.status === 'in_progress' ? 'bg-blue-500/20 text-blue-400' :
                      'bg-red-500/20 text-red-400'
                    }`}>
                      {selectedProject.status}
                    </span>
                  </div>
                  <h2 className="text-xl font-bold text-white">{selectedProject.project_title}</h2>
                  <p className="text-[#94A3B8] text-sm">{selectedProject.business_name}</p>
                </div>
                <button onClick={() => { setShowProjectModal(false); setSelectedProject(null); }} className="text-[#94A3B8] hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6 space-y-6">
                {/* Contact Info */}
                <div className="bg-[#060F22] rounded-xl p-4">
                  <h3 className="text-white font-medium mb-3">Contact Information</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-[#94A3B8]">Contact Name</p>
                      <p className="text-white">{selectedProject.contact_name}</p>
                    </div>
                    <div>
                      <p className="text-[#94A3B8]">Email</p>
                      <p className="text-white">{selectedProject.contact_email}</p>
                    </div>
                    <div>
                      <p className="text-[#94A3B8]">Phone</p>
                      <p className="text-white">{selectedProject.contact_phone || 'Not provided'}</p>
                    </div>
                    <div>
                      <p className="text-[#94A3B8]">Website</p>
                      <p className="text-white">{selectedProject.website_url || 'Not provided'}</p>
                    </div>
                  </div>
                </div>

                {/* Project Details */}
                <div className="bg-[#060F22] rounded-xl p-4">
                  <h3 className="text-white font-medium mb-3">Project Details</h3>
                  <div className="grid grid-cols-3 gap-4 text-sm mb-4">
                    <div>
                      <p className="text-[#94A3B8]">Type</p>
                      <p className="text-white capitalize">{selectedProject.project_type}</p>
                    </div>
                    <div>
                      <p className="text-[#94A3B8]">Timeline</p>
                      <p className="text-white">{selectedProject.timeline}</p>
                    </div>
                    <div>
                      <p className="text-[#94A3B8]">Budget</p>
                      <p className="text-white">{selectedProject.budget_range || 'Not specified'}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-[#94A3B8] mb-2">Description</p>
                    <p className="text-white text-sm">{selectedProject.project_description}</p>
                  </div>
                </div>

                {/* Applications */}
                {selectedProject.applications?.length > 0 && (
                  <div className="bg-[#060F22] rounded-xl p-4">
                    <h3 className="text-white font-medium mb-3">Applications ({selectedProject.applications.length})</h3>
                    <div className="space-y-3">
                      {selectedProject.applications.map((app) => (
                        <div key={app.id} className="flex items-center justify-between p-3 bg-[#0A1F44] rounded-lg">
                          <div>
                            <p className="text-white font-medium">{app.student_name}</p>
                            <p className="text-[#94A3B8] text-sm">{app.student_email}</p>
                            <p className="text-[#94A3B8] text-xs mt-1">{app.message}</p>
                          </div>
                          {app.status === 'pending' && selectedProject.status === 'approved' && (
                            <button
                              onClick={async () => {
                                try {
                                  await axios.put(
                                    `${API}/admin/projects/${selectedProject.id}/assign?student_id=${app.student_id}`,
                                    {},
                                    { headers: headers() }
                                  );
                                  toast.success('Student assigned to project!');
                                  fetchProjects();
                                  setShowProjectModal(false);
                                } catch (error) {
                                  toast.error('Failed to assign student');
                                }
                              }}
                              className="px-3 py-1 bg-[#00FFE0]/20 text-[#00FFE0] rounded-lg text-sm hover:bg-[#00FFE0]/30"
                            >
                              Assign
                            </button>
                          )}
                          {app.status === 'accepted' && (
                            <span className="px-2 py-1 bg-[#00FFE0]/20 text-[#00FFE0] rounded-full text-xs">Assigned</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Assigned Student */}
                {selectedProject.assigned_student && (
                  <div className="bg-[#00FFE0]/10 border border-[#00FFE0]/30 rounded-xl p-4">
                    <h3 className="text-[#00FFE0] font-medium mb-2">Assigned Student</h3>
                    <p className="text-white">{selectedProject.assigned_student.name}</p>
                    <p className="text-[#94A3B8] text-sm">{selectedProject.assigned_student.email}</p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-3">
                  {selectedProject.status === 'pending' && (
                    <>
                      <button
                        onClick={() => { updateProjectStatus(selectedProject.id, 'approved'); setShowProjectModal(false); }}
                        className="flex-1 bg-green-500 text-white px-4 py-2 rounded-xl hover:bg-green-600"
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => { updateProjectStatus(selectedProject.id, 'rejected'); setShowProjectModal(false); }}
                        className="flex-1 bg-red-500 text-white px-4 py-2 rounded-xl hover:bg-red-600"
                      >
                        Reject
                      </button>
                    </>
                  )}
                  {selectedProject.status === 'in_progress' && (
                    <button
                      onClick={() => { updateProjectStatus(selectedProject.id, 'completed'); setShowProjectModal(false); }}
                      className="flex-1 bg-[#00FFE0] text-[#0A1F44] px-4 py-2 rounded-xl hover:bg-[#00FFE0]/80"
                    >
                      Mark Completed
                    </button>
                  )}
                  <button
                    onClick={() => setShowProjectModal(false)}
                    className="px-4 py-2 bg-[#060F22] text-[#94A3B8] rounded-xl border border-[#00AEEF]/20"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Student Gamification Modal */}
        {showGamificationModal && selectedStudentGamification && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-[#0A1F44] border border-[#00AEEF]/20 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-6 border-b border-[#00AEEF]/20">
                <div>
                  <h2 className="text-xl font-bold text-white flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-[#FFD700]" />
                    {selectedStudentGamification.student_name}&apos;s Achievements
                  </h2>
                  <p className="text-[#94A3B8] text-sm">{selectedStudentGamification.student_email}</p>
                </div>
                <button onClick={() => { setShowGamificationModal(false); setSelectedStudentGamification(null); }} className="text-[#94A3B8] hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6 space-y-6">
                {/* Level & Points */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-[#060F22] rounded-xl p-4 text-center">
                    <div className="w-12 h-12 rounded-full mx-auto mb-2 flex items-center justify-center"
                      style={{ backgroundColor: `${selectedStudentGamification.level?.color}30` }}>
                      <Star className="w-6 h-6" style={{ color: selectedStudentGamification.level?.color }} />
                    </div>
                    <p className="text-lg font-bold" style={{ color: selectedStudentGamification.level?.color }}>
                      {selectedStudentGamification.level?.name}
                    </p>
                    <p className="text-xs text-[#94A3B8]">Level {selectedStudentGamification.level?.level}</p>
                  </div>
                  <div className="bg-[#060F22] rounded-xl p-4 text-center">
                    <div className="flex items-center justify-center gap-1 mb-2">
                      <Zap className="w-6 h-6 text-[#FF6A00]" />
                    </div>
                    <p className="text-2xl font-bold text-white">{selectedStudentGamification.total_points}</p>
                    <p className="text-xs text-[#94A3B8]">Total Points</p>
                  </div>
                  <div className="bg-[#060F22] rounded-xl p-4 text-center">
                    <div className="flex items-center justify-center gap-1 mb-2">
                      <Trophy className="w-6 h-6 text-[#FFD700]" />
                    </div>
                    <p className="text-2xl font-bold text-white">#{selectedStudentGamification.rank}</p>
                    <p className="text-xs text-[#94A3B8]">Leaderboard Rank</p>
                  </div>
                </div>

                {/* Badges */}
                <div>
                  <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                    <Medal className="w-4 h-4 text-[#FFD700]" />
                    Badges ({selectedStudentGamification.badges_earned}/{selectedStudentGamification.badges_total})
                  </h3>
                  <div className="grid grid-cols-4 gap-2">
                    {selectedStudentGamification.badges.filter(b => b.earned).map((badge) => (
                      <div key={badge.id} className="bg-[#FFD700]/10 border border-[#FFD700]/30 rounded-lg p-2 text-center">
                        <Trophy className="w-6 h-6 text-[#FFD700] mx-auto mb-1" />
                        <p className="text-xs text-white font-medium truncate">{badge.name}</p>
                      </div>
                    ))}
                    {selectedStudentGamification.badges.filter(b => b.earned).length === 0 && (
                      <p className="text-[#94A3B8] text-sm col-span-4">No badges earned yet</p>
                    )}
                  </div>
                </div>

                {/* Recent Activity */}
                <div>
                  <h3 className="text-white font-medium mb-3">Recent Activity</h3>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {selectedStudentGamification.recent_activity.map((activity) => (
                      <div key={activity.id} className="flex items-center justify-between bg-[#060F22] rounded-lg p-3">
                        <div className="flex items-center gap-2">
                          <Zap className="w-4 h-4 text-[#00FFE0]" />
                          <span className="text-sm text-[#94A3B8]">{activity.description || activity.action}</span>
                        </div>
                        <span className="text-[#00FFE0] font-medium">+{activity.points}</span>
                      </div>
                    ))}
                    {selectedStudentGamification.recent_activity.length === 0 && (
                      <p className="text-[#94A3B8] text-sm">No recent activity</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Export Attendance Modal */}
        {showExportModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-[#0A1F44] border border-[#00AEEF]/20 rounded-2xl max-w-md w-full">
              <div className="flex items-center justify-between p-6 border-b border-[#00AEEF]/20">
                <div>
                  <h2 className="text-xl font-bold text-white flex items-center gap-2">
                    <Download className="w-5 h-5 text-[#00AEEF]" />
                    Export Attendance
                  </h2>
                  <p className="text-[#94A3B8] text-sm">Download attendance records for grant reporting</p>
                </div>
                <button onClick={() => setShowExportModal(false)} className="text-[#94A3B8] hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6 space-y-6">
                {/* Date Range Preset */}
                <div>
                  <label className="block text-sm text-[#94A3B8] mb-2">Select Date Range</label>
                  <select
                    value={exportDateRange.preset}
                    onChange={(e) => setExportDateRange({ ...exportDateRange, preset: e.target.value })}
                    data-testid="export-date-preset"
                    className="w-full bg-[#060F22] border border-[#00AEEF]/30 text-white rounded-xl px-4 py-3"
                  >
                    <option value="today">Today</option>
                    <option value="this_week">This Week</option>
                    <option value="this_month">This Month</option>
                    <option value="last_month">Last Month</option>
                    <option value="all_time">All Time</option>
                    <option value="custom">Custom Range</option>
                  </select>
                </div>

                {/* Custom Date Range */}
                {exportDateRange.preset === 'custom' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-[#94A3B8] mb-2">Start Date</label>
                      <input
                        type="date"
                        value={exportDateRange.startDate}
                        onChange={(e) => setExportDateRange({ ...exportDateRange, startDate: e.target.value })}
                        data-testid="export-start-date"
                        className="w-full bg-[#060F22] border border-[#00AEEF]/30 text-white rounded-xl px-4 py-3"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-[#94A3B8] mb-2">End Date</label>
                      <input
                        type="date"
                        value={exportDateRange.endDate}
                        onChange={(e) => setExportDateRange({ ...exportDateRange, endDate: e.target.value })}
                        data-testid="export-end-date"
                        className="w-full bg-[#060F22] border border-[#00AEEF]/30 text-white rounded-xl px-4 py-3"
                      />
                    </div>
                  </div>
                )}

                {/* Export Info */}
                <div className="bg-[#060F22]/50 rounded-xl p-4 text-sm">
                  <p className="text-[#94A3B8] mb-2">Report will include:</p>
                  <ul className="text-[#94A3B8] space-y-1 ml-4 list-disc">
                    <li>Student Name & Email</li>
                    <li>Date, Sign-In & Sign-Out Times</li>
                    <li>Hours Present (calculated)</li>
                    <li>Attendance Status</li>
                    <li>Program & Notes</li>
                    <li>Summary Statistics</li>
                  </ul>
                </div>

                {/* Export Buttons */}
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() => handleExport('csv')}
                    disabled={exportLoading}
                    data-testid="export-csv-btn"
                    className="flex items-center justify-center gap-2 bg-[#00FFE0] text-[#0A1F44] font-bold px-4 py-3 rounded-xl hover:bg-[#00FFE0]/80 transition-all disabled:opacity-50"
                  >
                    {exportLoading ? (
                      <span className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#0A1F44]"></span>
                    ) : (
                      <>
                        <FileSpreadsheet className="w-5 h-5" />
                        Download CSV
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => handleExport('pdf')}
                    disabled={exportLoading}
                    data-testid="export-pdf-btn"
                    className="flex items-center justify-center gap-2 bg-[#FF6A00] text-white font-bold px-4 py-3 rounded-xl hover:bg-[#FF6A00]/80 transition-all disabled:opacity-50"
                  >
                    {exportLoading ? (
                      <span className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></span>
                    ) : (
                      <>
                        <FileText className="w-5 h-5" />
                        Download PDF
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Quiz Modal */}
        <QuizModal
          show={showQuizModal}
          quiz={selectedModule?.quiz}
          course={selectedCourse}
          module={selectedModule}
          onClose={() => { setShowQuizModal(false); setSelectedCourse(null); setSelectedModule(null); }}
          onSave={(data) => saveQuiz(selectedCourse.id, selectedModule.id, data)}
        />

        {/* User Detail Modal */}
        {showUserModal && selectedUser && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-[#0A1F44] border border-[#00AEEF]/20 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-6 border-b border-[#00AEEF]/20 sticky top-0 bg-[#0A1F44] z-10">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#00AEEF] to-[#00FFE0] flex items-center justify-center text-white text-2xl font-bold">
                    {selectedUser.name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">{selectedUser.name}</h2>
                    <p className="text-[#94A3B8]">{selectedUser.email}</p>
                    <div className="flex gap-2 mt-1">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        selectedUser.role === 'student' ? 'bg-[#00FFE0]/20 text-[#00FFE0]' :
                        selectedUser.role === 'teacher' ? 'bg-[#FF6A00]/20 text-[#FF6A00]' :
                        'bg-purple-500/20 text-purple-400'
                      }`}>
                        {selectedUser.role?.charAt(0).toUpperCase() + selectedUser.role?.slice(1)}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        selectedUser.status === 'archived' ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'
                      }`}>
                        {selectedUser.status === 'archived' ? 'Archived' : 'Active'}
                      </span>
                    </div>
                  </div>
                </div>
                <button onClick={() => setShowUserModal(false)} className="p-2 text-[#94A3B8] hover:text-white rounded-lg">
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* Quick Actions */}
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => { setShowUserModal(false); setShowUserEditModal(true); }}
                    className="px-4 py-2 bg-[#FF6A00] text-white rounded-lg hover:bg-[#FF6A00]/80 flex items-center gap-2"
                  >
                    <Edit2 className="w-4 h-4" />
                    Edit User
                  </button>
                  <button
                    onClick={() => { setShowUserModal(false); setShowUserMessageModal(true); }}
                    className="px-4 py-2 bg-[#00AEEF] text-white rounded-lg hover:bg-[#00AEEF]/80 flex items-center gap-2"
                  >
                    <MessageSquare className="w-4 h-4" />
                    Send Message
                  </button>
                  <button
                    onClick={() => { setShowUserModal(false); setShowUserPasswordModal(true); }}
                    className="px-4 py-2 bg-[#060F22] text-white border border-[#00AEEF]/30 rounded-lg hover:bg-[#00AEEF]/20 flex items-center gap-2"
                  >
                    <Key className="w-4 h-4" />
                    Reset Password
                  </button>
                  {selectedUser.role === 'student' && (
                    <button
                      onClick={() => { setShowUserModal(false); setShowUserCertificateModal(true); }}
                      className="px-4 py-2 bg-[#00FFE0] text-[#0A1F44] rounded-lg hover:bg-[#00FFE0]/80 flex items-center gap-2"
                    >
                      <Award className="w-4 h-4" />
                      Issue Certificate
                    </button>
                  )}
                  {selectedUser.status !== 'archived' ? (
                    <button
                      onClick={() => archiveUser(selectedUser.id)}
                      className="px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 flex items-center gap-2"
                    >
                      <Archive className="w-4 h-4" />
                      Archive User
                    </button>
                  ) : (
                    <button
                      onClick={() => activateUser(selectedUser.id)}
                      className="px-4 py-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 flex items-center gap-2"
                    >
                      <UserCheck className="w-4 h-4" />
                      Reactivate User
                    </button>
                  )}
                </div>

                {/* User Info */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-[#060F22] rounded-xl p-4 space-y-3">
                    <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                      <User className="w-5 h-5 text-[#00AEEF]" />
                      User Information
                    </h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-[#94A3B8]">Email:</span>
                        <span className="text-white">{selectedUser.email}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[#94A3B8]">Phone:</span>
                        <span className="text-white">{selectedUser.phone || 'Not provided'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[#94A3B8]">Registered:</span>
                        <span className="text-white">{selectedUser.created_at ? new Date(selectedUser.created_at).toLocaleDateString() : 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[#94A3B8]">Last Login:</span>
                        <span className="text-white">{selectedUser.last_login ? new Date(selectedUser.last_login).toLocaleString() : 'Never'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Gamification Stats for Students */}
                  {selectedUser.role === 'student' && selectedUser.gamification && (
                    <div className="bg-[#060F22] rounded-xl p-4 space-y-3">
                      <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                        <Trophy className="w-5 h-5 text-[#FF6A00]" />
                        Gamification
                      </h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between items-center">
                          <span className="text-[#94A3B8]">Level:</span>
                          <span className="font-bold" style={{ color: selectedUser.gamification.level_color }}>
                            {selectedUser.gamification.level_name} (Lvl {selectedUser.gamification.level})
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-[#94A3B8]">Total Points:</span>
                          <span className="text-white font-bold">{selectedUser.gamification.total_points?.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-[#94A3B8]">Streak:</span>
                          <span className="text-white">{selectedUser.gamification.streak_days} days</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-[#94A3B8]">Leaderboard:</span>
                          <span className="text-[#00FFE0]">#{selectedUser.gamification.leaderboard_position}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-[#94A3B8]">Badges:</span>
                          <span className="text-white">{selectedUser.gamification.badges?.length || 0}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Learning Progress for Students */}
                {selectedUser.role === 'student' && selectedUser.learning_progress && (
                  <div className="bg-[#060F22] rounded-xl p-4 space-y-4">
                    <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                      <Target className="w-5 h-5 text-[#00FFE0]" />
                      Learning Progress
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center p-3 bg-[#0A1F44] rounded-lg">
                        <p className="text-2xl font-bold text-white">{selectedUser.learning_progress.total_courses_started}</p>
                        <p className="text-xs text-[#94A3B8]">Courses Started</p>
                      </div>
                      <div className="text-center p-3 bg-[#0A1F44] rounded-lg">
                        <p className="text-2xl font-bold text-white">{selectedUser.learning_progress.total_lessons_completed}</p>
                        <p className="text-xs text-[#94A3B8]">Lessons Completed</p>
                      </div>
                      <div className="text-center p-3 bg-[#0A1F44] rounded-lg">
                        <p className="text-2xl font-bold text-white">{selectedUser.learning_progress.total_hours_spent}h</p>
                        <p className="text-xs text-[#94A3B8]">Hours Spent</p>
                      </div>
                      <div className="text-center p-3 bg-[#0A1F44] rounded-lg">
                        <p className="text-2xl font-bold text-white">{selectedUser.learning_progress.certificates?.length || 0}</p>
                        <p className="text-xs text-[#94A3B8]">Certificates</p>
                      </div>
                    </div>

                    {/* Course Progress */}
                    {selectedUser.learning_progress.courses?.length > 0 && (
                      <div className="space-y-3">
                        <h4 className="text-sm font-medium text-[#94A3B8]">Course Progress</h4>
                        {selectedUser.learning_progress.courses.map((course, idx) => (
                          <div key={idx} className="bg-[#0A1F44] rounded-lg p-3">
                            <div className="flex justify-between items-center mb-2">
                              <span className="text-white font-medium">{course.course_title}</span>
                              <span className="text-[#00FFE0] font-bold">{course.progress_percent}%</span>
                            </div>
                            <div className="w-full h-2 bg-[#060F22] rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-gradient-to-r from-[#00AEEF] to-[#00FFE0] rounded-full transition-all"
                                style={{ width: `${course.progress_percent}%` }}
                              ></div>
                            </div>
                            {course.current_lesson && (
                              <p className="text-xs text-[#94A3B8] mt-1">
                                Current: {course.current_lesson.title}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Badges */}
                {selectedUser.gamification?.badges?.length > 0 && (
                  <div className="bg-[#060F22] rounded-xl p-4 space-y-3">
                    <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                      <Medal className="w-5 h-5 text-yellow-400" />
                      Badges Earned ({selectedUser.gamification.badges.length})
                    </h3>
                    <div className="flex flex-wrap gap-3">
                      {selectedUser.gamification.badges.map((badge, idx) => (
                        <div key={idx} className="bg-[#0A1F44] rounded-lg p-3 flex items-center gap-3 min-w-[200px]">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center">
                            <Star className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <p className="text-white font-medium">{badge.name}</p>
                            <p className="text-xs text-[#94A3B8]">{badge.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* User Edit Modal */}
        {showUserEditModal && selectedUser && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-[#0A1F44] border border-[#00AEEF]/20 rounded-2xl max-w-md w-full">
              <div className="flex items-center justify-between p-6 border-b border-[#00AEEF]/20">
                <h2 className="text-xl font-bold text-white">Edit User</h2>
                <button onClick={() => setShowUserEditModal(false)} className="p-2 text-[#94A3B8] hover:text-white rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.target);
                  updateUser(selectedUser.id, {
                    name: formData.get('name'),
                    email: formData.get('email'),
                    phone: formData.get('phone'),
                    status: formData.get('status')
                  });
                }}
                className="p-6 space-y-4"
              >
                <div>
                  <label className="block text-sm text-[#94A3B8] mb-1">Name</label>
                  <input
                    type="text"
                    name="name"
                    defaultValue={selectedUser.name}
                    className="w-full bg-[#060F22] border border-[#00AEEF]/30 text-white rounded-xl px-4 py-2.5 focus:border-[#00AEEF]"
                    data-testid="edit-user-name"
                  />
                </div>
                <div>
                  <label className="block text-sm text-[#94A3B8] mb-1">Email</label>
                  <input
                    type="email"
                    name="email"
                    defaultValue={selectedUser.email}
                    className="w-full bg-[#060F22] border border-[#00AEEF]/30 text-white rounded-xl px-4 py-2.5 focus:border-[#00AEEF]"
                    data-testid="edit-user-email"
                  />
                </div>
                <div>
                  <label className="block text-sm text-[#94A3B8] mb-1">Phone</label>
                  <input
                    type="text"
                    name="phone"
                    defaultValue={selectedUser.phone || ''}
                    className="w-full bg-[#060F22] border border-[#00AEEF]/30 text-white rounded-xl px-4 py-2.5 focus:border-[#00AEEF]"
                    data-testid="edit-user-phone"
                  />
                </div>
                <div>
                  <label className="block text-sm text-[#94A3B8] mb-1">Status</label>
                  <select
                    name="status"
                    defaultValue={selectedUser.status || 'active'}
                    className="w-full bg-[#060F22] border border-[#00AEEF]/30 text-white rounded-xl px-4 py-2.5 focus:border-[#00AEEF]"
                    data-testid="edit-user-status"
                  >
                    <option value="active">Active</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowUserEditModal(false)}
                    className="flex-1 bg-[#060F22] text-[#94A3B8] px-4 py-2.5 rounded-xl border border-[#00AEEF]/20"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-[#FF6A00] text-white px-4 py-2.5 rounded-xl"
                    data-testid="save-user-btn"
                  >
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* User Message Modal */}
        {showUserMessageModal && selectedUser && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-[#0A1F44] border border-[#00AEEF]/20 rounded-2xl max-w-md w-full">
              <div className="flex items-center justify-between p-6 border-b border-[#00AEEF]/20">
                <h2 className="text-xl font-bold text-white">Send Message to {selectedUser.name}</h2>
                <button onClick={() => setShowUserMessageModal(false)} className="p-2 text-[#94A3B8] hover:text-white rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.target);
                  sendUserMessage(selectedUser.id, formData.get('subject'), formData.get('message'));
                }}
                className="p-6 space-y-4"
              >
                <div>
                  <label className="block text-sm text-[#94A3B8] mb-1">Subject</label>
                  <input
                    type="text"
                    name="subject"
                    required
                    className="w-full bg-[#060F22] border border-[#00AEEF]/30 text-white rounded-xl px-4 py-2.5 focus:border-[#00AEEF]"
                    placeholder="Message subject..."
                    data-testid="message-subject"
                  />
                </div>
                <div>
                  <label className="block text-sm text-[#94A3B8] mb-1">Message</label>
                  <textarea
                    name="message"
                    required
                    rows={5}
                    className="w-full bg-[#060F22] border border-[#00AEEF]/30 text-white rounded-xl px-4 py-2.5 focus:border-[#00AEEF]"
                    placeholder="Type your message..."
                    data-testid="message-body"
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowUserMessageModal(false)}
                    className="flex-1 bg-[#060F22] text-[#94A3B8] px-4 py-2.5 rounded-xl border border-[#00AEEF]/20"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-[#00AEEF] text-white px-4 py-2.5 rounded-xl"
                    data-testid="send-message-btn"
                  >
                    Send Message
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* User Password Reset Modal */}
        {showUserPasswordModal && selectedUser && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-[#0A1F44] border border-[#00AEEF]/20 rounded-2xl max-w-md w-full">
              <div className="flex items-center justify-between p-6 border-b border-[#00AEEF]/20">
                <h2 className="text-xl font-bold text-white">Reset Password</h2>
                <button onClick={() => setShowUserPasswordModal(false)} className="p-2 text-[#94A3B8] hover:text-white rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.target);
                  resetUserPassword(selectedUser.id, formData.get('password'));
                }}
                className="p-6 space-y-4"
              >
                <p className="text-[#94A3B8]">
                  Enter a new password for <span className="text-white font-medium">{selectedUser.name}</span>.
                  They will receive an email with the new password.
                </p>
                <div>
                  <label className="block text-sm text-[#94A3B8] mb-1">New Password</label>
                  <input
                    type="text"
                    name="password"
                    required
                    minLength={6}
                    className="w-full bg-[#060F22] border border-[#00AEEF]/30 text-white rounded-xl px-4 py-2.5 focus:border-[#00AEEF]"
                    placeholder="Enter new password (min 6 chars)"
                    data-testid="new-password-input"
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowUserPasswordModal(false)}
                    className="flex-1 bg-[#060F22] text-[#94A3B8] px-4 py-2.5 rounded-xl border border-[#00AEEF]/20"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-[#FF6A00] text-white px-4 py-2.5 rounded-xl"
                    data-testid="reset-password-btn"
                  >
                    Reset Password
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* User Certificate Issue Modal */}
        {showUserCertificateModal && selectedUser && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-[#0A1F44] border border-[#00AEEF]/20 rounded-2xl max-w-md w-full">
              <div className="flex items-center justify-between p-6 border-b border-[#00AEEF]/20">
                <h2 className="text-xl font-bold text-white">Issue Certificate</h2>
                <button onClick={() => setShowUserCertificateModal(false)} className="p-2 text-[#94A3B8] hover:text-white rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.target);
                  issueCertificate(selectedUser.id, formData.get('course_id'), formData.get('notes'));
                }}
                className="p-6 space-y-4"
              >
                <p className="text-[#94A3B8]">
                  Issue a certificate to <span className="text-white font-medium">{selectedUser.name}</span>.
                </p>
                <div>
                  <label className="block text-sm text-[#94A3B8] mb-1">Course</label>
                  <select
                    name="course_id"
                    required
                    className="w-full bg-[#060F22] border border-[#00AEEF]/30 text-white rounded-xl px-4 py-2.5 focus:border-[#00AEEF]"
                    data-testid="certificate-course-select"
                  >
                    <option value="">Select a course...</option>
                    {courses.map((course) => (
                      <option key={course.id} value={course.id}>{course.title}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-[#94A3B8] mb-1">Notes (Optional)</label>
                  <textarea
                    name="notes"
                    rows={3}
                    className="w-full bg-[#060F22] border border-[#00AEEF]/30 text-white rounded-xl px-4 py-2.5 focus:border-[#00AEEF]"
                    placeholder="Any additional notes..."
                    data-testid="certificate-notes"
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowUserCertificateModal(false)}
                    className="flex-1 bg-[#060F22] text-[#94A3B8] px-4 py-2.5 rounded-xl border border-[#00AEEF]/20"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-[#00FFE0] text-[#0A1F44] px-4 py-2.5 rounded-xl font-medium"
                    data-testid="issue-certificate-btn"
                  >
                    Issue Certificate
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Course Modal Component
const CourseModal = ({ show, course, onClose, onSave }) => {
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [duration, setDuration] = useState('');

  useEffect(() => {
    if (course) {
      setTitle(course.title || '');
      setSlug(course.slug || '');
      setDescription(course.description || '');
      setDuration(course.duration || '');
    } else {
      setTitle('');
      setSlug('');
      setDescription('');
      setDuration('');
    }
  }, [course, show]);

  const generateSlug = (text) => {
    return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  };

  const handleTitleChange = (e) => {
    const newTitle = e.target.value;
    setTitle(newTitle);
    if (!course) {
      setSlug(generateSlug(newTitle));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ title, slug, description, duration });
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-[#0A1F44] border border-[#00AEEF]/20 rounded-2xl max-w-lg w-full">
        <div className="flex items-center justify-between p-6 border-b border-[#00AEEF]/20">
          <h2 className="text-xl font-bold text-white">{course ? 'Edit Course' : 'Create Course'}</h2>
          <button onClick={onClose} className="p-2 text-[#94A3B8] hover:text-white rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm text-[#94A3B8] mb-1">Course Title</label>
            <input
              type="text"
              value={title}
              onChange={handleTitleChange}
              required
              data-testid="course-title-input"
              className="w-full bg-[#060F22] border border-[#00AEEF]/30 text-white rounded-xl px-4 py-2.5 focus:border-[#00AEEF] focus:ring-1 focus:ring-[#00AEEF]"
              placeholder="e.g., Web Development Foundations"
            />
          </div>
          <div>
            <label className="block text-sm text-[#94A3B8] mb-1">URL Slug</label>
            <input
              type="text"
              value={slug}
              onChange={(e) => setSlug(generateSlug(e.target.value))}
              required
              disabled={!!course}
              data-testid="course-slug-input"
              className="w-full bg-[#060F22] border border-[#00AEEF]/30 text-white rounded-xl px-4 py-2.5 focus:border-[#00AEEF] focus:ring-1 focus:ring-[#00AEEF] disabled:opacity-50"
              placeholder="web-development-foundations"
            />
          </div>
          <div>
            <label className="block text-sm text-[#94A3B8] mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
              rows={3}
              data-testid="course-description-input"
              className="w-full bg-[#060F22] border border-[#00AEEF]/30 text-white rounded-xl px-4 py-2.5 focus:border-[#00AEEF] focus:ring-1 focus:ring-[#00AEEF]"
              placeholder="Brief description of the course..."
            />
          </div>
          <div>
            <label className="block text-sm text-[#94A3B8] mb-1">Duration</label>
            <input
              type="text"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              required
              data-testid="course-duration-input"
              className="w-full bg-[#060F22] border border-[#00AEEF]/30 text-white rounded-xl px-4 py-2.5 focus:border-[#00AEEF] focus:ring-1 focus:ring-[#00AEEF]"
              placeholder="e.g., 8 weeks"
            />
          </div>
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-[#060F22] text-[#94A3B8] px-4 py-2.5 rounded-xl border border-[#00AEEF]/20 hover:text-white transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="save-course-btn"
              className="flex-1 bg-[#FF6A00] text-white px-4 py-2.5 rounded-xl hover:shadow-[0_0_20px_rgba(255,106,0,0.3)] transition-all"
            >
              {course ? 'Update Course' : 'Create Course'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Module Modal Component
const ModuleModal = ({ show, module, course, onClose, onSave }) => {
  const [title, setTitle] = useState('');
  const [order, setOrder] = useState(1);

  useEffect(() => {
    if (module) {
      setTitle(module.title || '');
      setOrder(module.order || 1);
    } else if (course) {
      setTitle('');
      setOrder((course.modules?.length || 0) + 1);
    }
  }, [module, course, show]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ title, order: parseInt(order) });
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-[#0A1F44] border border-[#00AEEF]/20 rounded-2xl max-w-lg w-full">
        <div className="flex items-center justify-between p-6 border-b border-[#00AEEF]/20">
          <h2 className="text-xl font-bold text-white">{module ? 'Edit Module' : 'Add Module'}</h2>
          <button onClick={onClose} className="p-2 text-[#94A3B8] hover:text-white rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm text-[#94A3B8] mb-1">Module Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              data-testid="module-title-input"
              className="w-full bg-[#060F22] border border-[#00AEEF]/30 text-white rounded-xl px-4 py-2.5 focus:border-[#00AEEF] focus:ring-1 focus:ring-[#00AEEF]"
              placeholder="e.g., Week 1-2: HTML & CSS Basics"
            />
          </div>
          <div>
            <label className="block text-sm text-[#94A3B8] mb-1">Order</label>
            <input
              type="number"
              value={order}
              onChange={(e) => setOrder(e.target.value)}
              required
              min="1"
              data-testid="module-order-input"
              className="w-full bg-[#060F22] border border-[#00AEEF]/30 text-white rounded-xl px-4 py-2.5 focus:border-[#00AEEF] focus:ring-1 focus:ring-[#00AEEF]"
            />
          </div>
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-[#060F22] text-[#94A3B8] px-4 py-2.5 rounded-xl border border-[#00AEEF]/20 hover:text-white transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="save-module-btn"
              className="flex-1 bg-[#00AEEF] text-white px-4 py-2.5 rounded-xl hover:shadow-[0_0_20px_rgba(0,174,239,0.3)] transition-all"
            >
              {module ? 'Update Module' : 'Add Module'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Lesson Modal Component
const LessonModal = ({ show, lesson, course, module, onClose, onSave }) => {
  const [title, setTitle] = useState('');
  const [type, setType] = useState('video');
  const [content, setContent] = useState('');
  const [duration, setDuration] = useState('');
  const [order, setOrder] = useState(1);

  useEffect(() => {
    if (lesson) {
      setTitle(lesson.title || '');
      setType(lesson.type || 'video');
      setContent(lesson.content || '');
      setDuration(lesson.duration || '');
      setOrder(lesson.order || 1);
    } else if (module) {
      setTitle('');
      setType('video');
      setContent('');
      setDuration('');
      setOrder((module.lessons?.length || 0) + 1);
    }
  }, [lesson, module, show]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ title, type, content, duration, order: parseInt(order) });
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-[#0A1F44] border border-[#00AEEF]/20 rounded-2xl max-w-lg w-full max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-[#00AEEF]/20">
          <h2 className="text-xl font-bold text-white">{lesson ? 'Edit Lesson' : 'Add Lesson'}</h2>
          <button onClick={onClose} className="p-2 text-[#94A3B8] hover:text-white rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm text-[#94A3B8] mb-1">Lesson Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              data-testid="lesson-title-input"
              className="w-full bg-[#060F22] border border-[#00AEEF]/30 text-white rounded-xl px-4 py-2.5 focus:border-[#00AEEF] focus:ring-1 focus:ring-[#00AEEF]"
              placeholder="e.g., Introduction to HTML"
            />
          </div>
          <div>
            <label className="block text-sm text-[#94A3B8] mb-1">Type</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              data-testid="lesson-type-select"
              className="w-full bg-[#060F22] border border-[#00AEEF]/30 text-white rounded-xl px-4 py-2.5 focus:border-[#00AEEF] focus:ring-1 focus:ring-[#00AEEF]"
            >
              <option value="video">Video</option>
              <option value="reading">Reading</option>
              <option value="assignment">Assignment</option>
            </select>
          </div>
          <div>
            <label className="block text-sm text-[#94A3B8] mb-1">Content</label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              required
              rows={4}
              data-testid="lesson-content-input"
              className="w-full bg-[#060F22] border border-[#00AEEF]/30 text-white rounded-xl px-4 py-2.5 focus:border-[#00AEEF] focus:ring-1 focus:ring-[#00AEEF]"
              placeholder="Lesson content or instructions..."
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-[#94A3B8] mb-1">Duration</label>
              <input
                type="text"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                required
                data-testid="lesson-duration-input"
                className="w-full bg-[#060F22] border border-[#00AEEF]/30 text-white rounded-xl px-4 py-2.5 focus:border-[#00AEEF] focus:ring-1 focus:ring-[#00AEEF]"
                placeholder="e.g., 15 min"
              />
            </div>
            <div>
              <label className="block text-sm text-[#94A3B8] mb-1">Order</label>
              <input
                type="number"
                value={order}
                onChange={(e) => setOrder(e.target.value)}
                required
                min="1"
                data-testid="lesson-order-input"
                className="w-full bg-[#060F22] border border-[#00AEEF]/30 text-white rounded-xl px-4 py-2.5 focus:border-[#00AEEF] focus:ring-1 focus:ring-[#00AEEF]"
              />
            </div>
          </div>
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-[#060F22] text-[#94A3B8] px-4 py-2.5 rounded-xl border border-[#00AEEF]/20 hover:text-white transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="save-lesson-btn"
              className="flex-1 bg-[#00FFE0] text-[#0A1F44] font-medium px-4 py-2.5 rounded-xl hover:shadow-[0_0_20px_rgba(0,255,224,0.3)] transition-all"
            >
              {lesson ? 'Update Lesson' : 'Add Lesson'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Quiz Modal Component
const QuizModal = ({ show, quiz, course, module, onClose, onSave }) => {
  const [title, setTitle] = useState('');
  const [questions, setQuestions] = useState([]);

  useEffect(() => {
    if (quiz) {
      setTitle(quiz.title || '');
      setQuestions(quiz.questions?.map(q => ({
        question: q.question,
        options: [...q.options],
        correct: q.correct
      })) || []);
    } else if (module) {
      setTitle(`${module.title} Quiz`);
      setQuestions([{ question: '', options: ['', '', '', ''], correct: 0 }]);
    }
  }, [quiz, module, show]);

  const addQuestion = () => {
    setQuestions([...questions, { question: '', options: ['', '', '', ''], correct: 0 }]);
  };

  const removeQuestion = (index) => {
    setQuestions(questions.filter((_, i) => i !== index));
  };

  const updateQuestion = (index, field, value) => {
    const updated = [...questions];
    updated[index][field] = value;
    setQuestions(updated);
  };

  const updateOption = (qIndex, oIndex, value) => {
    const updated = [...questions];
    updated[qIndex].options[oIndex] = value;
    setQuestions(updated);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ title, questions });
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-[#0A1F44] border border-[#00AEEF]/20 rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-[#00AEEF]/20 sticky top-0 bg-[#0A1F44] z-10">
          <h2 className="text-xl font-bold text-white">{quiz ? 'Edit Quiz' : 'Create Quiz'}</h2>
          <button onClick={onClose} className="p-2 text-[#94A3B8] hover:text-white rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="block text-sm text-[#94A3B8] mb-1">Quiz Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              data-testid="quiz-title-input"
              className="w-full bg-[#060F22] border border-[#00AEEF]/30 text-white rounded-xl px-4 py-2.5 focus:border-[#00AEEF] focus:ring-1 focus:ring-[#00AEEF]"
              placeholder="e.g., HTML & CSS Basics Quiz"
            />
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-white font-medium">Questions ({questions.length})</h3>
              <button
                type="button"
                onClick={addQuestion}
                className="flex items-center gap-1 text-sm text-[#00FFE0] hover:text-[#00FFE0]/80"
              >
                <Plus className="w-4 h-4" />
                Add Question
              </button>
            </div>

            {questions.map((q, qIndex) => (
              <div key={qIndex} className="bg-[#060F22]/50 rounded-xl p-4 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <label className="block text-xs text-[#94A3B8] mb-1">Question {qIndex + 1}</label>
                    <input
                      type="text"
                      value={q.question}
                      onChange={(e) => updateQuestion(qIndex, 'question', e.target.value)}
                      required
                      className="w-full bg-[#0A1F44] border border-[#00AEEF]/30 text-white rounded-lg px-3 py-2 text-sm focus:border-[#00AEEF]"
                      placeholder="Enter your question..."
                    />
                  </div>
                  {questions.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeQuestion(qIndex)}
                      className="p-1.5 text-red-400 hover:bg-red-400/20 rounded mt-5"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-2">
                  {q.options.map((opt, oIndex) => (
                    <div key={oIndex} className="flex items-center gap-2">
                      <input
                        type="radio"
                        name={`correct-${qIndex}`}
                        checked={q.correct === oIndex}
                        onChange={() => updateQuestion(qIndex, 'correct', oIndex)}
                        className="text-[#00FFE0]"
                      />
                      <input
                        type="text"
                        value={opt}
                        onChange={(e) => updateOption(qIndex, oIndex, e.target.value)}
                        required
                        className="flex-1 bg-[#0A1F44] border border-[#00AEEF]/30 text-white rounded-lg px-3 py-1.5 text-sm focus:border-[#00AEEF]"
                        placeholder={`Option ${oIndex + 1}`}
                      />
                    </div>
                  ))}
                </div>
                <p className="text-xs text-[#94A3B8]">Select the radio button for the correct answer</p>
              </div>
            ))}
          </div>

          <div className="flex gap-3 pt-4 sticky bottom-0 bg-[#0A1F44] py-4 -mb-6 -mx-6 px-6 border-t border-[#00AEEF]/20">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-[#060F22] text-[#94A3B8] px-4 py-2.5 rounded-xl border border-[#00AEEF]/20 hover:text-white transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="save-quiz-btn"
              className="flex-1 bg-[#FF6A00] text-white px-4 py-2.5 rounded-xl hover:shadow-[0_0_20px_rgba(255,106,0,0.3)] transition-all"
            >
              {quiz ? 'Update Quiz' : 'Save Quiz'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Admin;
